import React from "react";
import { newId } from "./lib/ids";
import { rekeyState, hasSeedIds } from "./lib/rekey";
import { roleOf, can, roleLabel, homeRouteFor, normalizeRole } from "./lib/roles";
import { CHANGELOG } from "./lib/changelog";
import { initTracking, trackPage, trackStateChange, track } from "./lib/track";
import { reportError, setCrashRedactor, sensitiveStrings } from "./lib/crash";   // crash reports → Ops & Health → App errors
import { startMediaSync } from "./lib/media";   // job photos → files, in the background
import { startFieldSync, runFieldOp, fieldView, useOutboxVersion } from "./lib/fieldsync";   // crew offline outbox (FW-007)
import { rescuePendingPhotos, pendingPhotosOf } from "./lib/outbox";
import { applyImportBatch, revertImportBatch } from "./lib/dataio";
import { planLimit, quoteLimit, canSaveProduct, canAddQuote } from "./lib/limits";
import { withQuoteDefaults } from "./lib/quotedetails";   // a new quote's valid-until date + default cover note
import { LeadEngineHome, LeadFindPage, LeadTodayPage, ProspectPage, OutreachPage } from "./registry";   // FW Admin → Lead Engine
import { PlatformAdminsPage } from "./registry";   // FW Admin → Admins
import { PlatformStatusPage } from "./registry";   // FW Admin → Status & To-do
import { NotificationsPage } from "./pushsettings";   // phone + desktop notifications (lib/push.ts, server/src/push.js)
import { pushCovers, pushOnHere } from "./lib/push";
import { VERTICALS, enabledVerticals, canSwitch, activeVertical, readLensPref, LENS_EVENT, routeHidden, stampVertical, verticalOf, inLens } from "./lib/verticals";   // the service switcher (docs/SERVICE_SWITCHER_PLAN.md)
import { LensGate, ConcreteEstimatePage, ConcreteQuoteNote, RateCardEditor, useSupportAdmin } from "./registry";
import { rateCardsOf, blocksOf, measuresOf } from "./lib/engine.concrete";   // the estimate snapshot a concrete job carries (lib/concrete.actuals.ts)
import { warrantyTermsFor, datedWarranty } from "./lib/concrete.actuals";
import { plansFromJob, mergePlans, rollPlansFor } from "./lib/careplans";   // a finished concrete job starts (or rolls) the customer's care plans (§13 item 15)
import { localYmd } from "./lib/dates";
import { AISuite, PhonePage, PhoneLinesAdminPage, AccountBilling, AuthScreen, AutomationPage, BusinessPage, CalendarView, CodesPage, CrewMode, CustomerHubSettings, Customers, Dashboard, EmailSequences, EstimatesPage, Estimator, EstimatorWidgetSettings, GlobalSearch, Guide, HelpBubble, Icon, Integrations, InventoryPage, JobPacketPage, PrintKitPage, PrintOrdersAdmin, LeadRadarPage, DataIOPage, ReviewsPage, GbpOptimizerPage, WebsitePage, PurchaseOrdersPage, LogoMark, LogoMarkLoop, MessagesPage, FinancingPage, ModelsPage, PermitCheck, ProductEditor, Products, PublicRoute, Quote, RecoveryGate, ReportsPage, SalesTeamPage, Settings, Sidebar, SpecsPage, SupportInbox, OpsPage, DeletionRequestsPage, CustomersAdminPage, LifecycleEmailsPage, GrowthPage, TVDisplay, TodoPage, Visualizer, WorkOrders, useAuth, Toaster, ConfirmHost, InstallNudge, installDialogA11y, FOUNDER_ENDS_LABEL, DemoBanner, DemoExpired, DemoOff, DemoWatermark, DemoTour, PageGuideButton, widgetPriceRanges, widgetRangeStamp, customerDocOf, signedDocOf, docBaseTotal, computeQuoteView, notify, todoUrgentCount } from "./registry";
/* ============================================================
   Fence Works — store + router + mount
   ============================================================ */
// demo mode (sandbox): decided at boot by lib/demo.ts. The sandbox keeps its
// own localStorage key so it can never mix with a real account's cached store.
const DEMO: any = window.VFP_DEMO || { active: false };
const LS_KEY = DEMO.active ? DEMO.stateKey : "vfp_state_v2";
// embed=1: the sandbox inside a frame on the marketing site — one screen, no
// sidebar / top bar / banner / walkthrough, a slim "open the full demo" strip.
// Sandbox only; a signed-in workspace never renders without its chrome.
const EMBED = DEMO.active && /[?&]embed=1(&|$)/.test(window.location.search || "");
const BUILTIN_PRODUCTS = window.VFP.PRODUCTS.slice();
const BUILTIN_IDS = BUILTIN_PRODUCTS.map((p: any) => p.id);
// the plan a state is on — the same resolution as ROUTE_GATE (window.VFP_PLAN,
// account.tsx); no plan table loaded → null → no limit
const planOfState = (s: any) => (window.VFP_PLAN ? window.VFP_PLAN.get(s) : null);

// Capture a plan/prefill handed off from the marketing signup
// (app.fenceworks.ai/?plan=pro&email=…) into localStorage *before* React renders,
// then clean the URL. The auth screen prefills from it, and App (once signed in)
// routes a paid plan to Stripe Checkout.
(function () {
  try {
    // legacy hash-era URLs (#/account?checkout=…, #/hub/<token>) → clean paths
    const h = window.location.hash || "";
    if (/^#\//.test(h)) window.history.replaceState({}, "", h.slice(1) + window.location.search);
    const p = new URLSearchParams(window.location.search);
    // team invite link (app.fenceworks.ai/?invite=<token>) — stash it; once the
    // person signs in / signs up, App accepts it and they join the team.
    if (p.get("invite")) { localStorage.setItem("fw_pending_invite", p.get("invite")); window.history.replaceState({}, "", window.location.pathname); }
    // Lead Engine claim (?claim=<token>, from "Make this my website" on the
    // preview we built them, via the service's /l/claim/ redirect — which also
    // brings ?plan=growth&email=): stash it; once they're signed in, App copies
    // that site into their workspace (claimPendingSite) before checkout.
    const claimTok = String(p.get("claim") || "").toLowerCase();
    if (/^[0-9a-f]{32}$/.test(claimTok)) localStorage.setItem("fw_pending_claim", claimTok);
    if (p.get("claim") && !p.get("plan") && !p.get("email")) window.history.replaceState({}, "", window.location.pathname + window.location.hash);
    // password-recovery link: Supabase puts type=recovery in the hash — flag it
    // SYNCHRONOUSLY (before the client consumes the hash and signs them in) so
    // the app can prompt for a new password right after.
    if (/type=recovery/.test(window.location.hash || "")) localStorage.setItem("fw_pw_recovery", "1");
    // Marketing-site signup handoff: the prefill (name/company/email + plan)
    // rides a short-lived cookie on .fenceworks.ai so no PII lands in the URL
    // (and therefore in analytics). The query carries only ?plan=&src=.
    try {
      const m = document.cookie.match(/(?:^|;\s*)fw_handoff=([^;]+)/);
      if (m) {
        const h = JSON.parse(decodeURIComponent(m[1]));
        if (h && h.plan) localStorage.setItem("fw_pending_plan", String(h.plan));
        // the managed plan is sold on fenceworks.ai/managed and billed by a Stripe
        // Payment Link, not by the app's checkout: remember the intent on the
        // signup's attribution so FW Admin and the signup alert say "managed"
        if (h && String(h.plan) === "managed") {
          const s = (v: any, n: number) => (v == null ? null : String(v).slice(0, n));
          h.attribution = { ...((h.attribution && typeof h.attribution === "object") ? h.attribution : {}), plan_intent: "managed",
            // the verified Checkout Session (managedlink.js): the Stripe customer to tie this workspace to, and the server's own proof it was paid
            ...(h.stripe_customer ? { stripe_customer: s(h.stripe_customer, 60) } : {}), ...(h.managed_session ? { checkout_session: s(h.managed_session, 80) } : {}), ...(h.managed_token ? { managed_token: s(h.managed_token, 400) } : {}) };
        }
        ["email", "name", "company"].forEach((k) => { if (h && h[k]) localStorage.setItem("fw_prefill_" + k, String(h[k])); });
        // Signup attribution — how they said they found us, plus the first-touch
        // UTM/referrer the marketing site captured on their very first page view.
        // Stashed here and handed to auth.signUp, which puts it in the user's
        // metadata; handle_new_user() then writes it onto the workspace row. Kept
        // out of the URL like the rest of the handoff so it never hits analytics.
        if (h && h.heard_about) localStorage.setItem("fw_prefill_heard", String(h.heard_about));
        if (h && h.heard_detail) localStorage.setItem("fw_prefill_heard_detail", String(h.heard_detail));
        if (h && h.attribution && typeof h.attribution === "object") {
          try { localStorage.setItem("fw_prefill_attribution", JSON.stringify(h.attribution)); } catch (e) {}
        }
        document.cookie = "fw_handoff=; path=/; max-age=0; domain=.fenceworks.ai";
        document.cookie = "fw_handoff=; path=/; max-age=0";
      }
    } catch (e) {}
    if (!p.get("plan") && !p.get("email")) { if (p.get("src")) window.history.replaceState({}, "", window.location.pathname + window.location.hash); return; }
    // A signup link in our own email (the scorecard follow-ups: ?plan=&email=
    // &utm_source=scorecard_nurture…) comes straight here, not through the
    // marketing site, so no handoff cookie says where it came from. Its UTM tags
    // become the signup's attribution unless the site already handed one over
    // (first touch wins). The landing path only: this query has an email in it.
    try {
      if (p.get("utm_source") && !localStorage.getItem("fw_prefill_attribution")) {
        const a: Record<string, string> = { first_seen: new Date().toISOString(), landing: window.location.pathname };
        ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => { const v = p.get(k); if (v) a[k] = String(v).slice(0, 200); });
        localStorage.setItem("fw_prefill_attribution", JSON.stringify(a));
      }
    } catch (e) {}
    if (p.get("plan")) localStorage.setItem("fw_pending_plan", p.get("plan"));
    ["email", "name", "company"].forEach((k) => { if (p.get(k)) localStorage.setItem("fw_prefill_" + k, p.get(k)); });
    window.history.replaceState({}, "", window.location.pathname + window.location.hash);
  } catch (e) {}
})();

/* Lead Engine claim: what to do with POST /leadengine-claim's answer. Pure
   (scripts/unit-claim.mjs). `clear` drops the stashed token, `retry` tries
   again shortly (the workspace may not exist yet right after signup, or the
   service hiccupped), `ok` means the site is now theirs. */
const CLAIM_READY = "Your new site is ready — review it and publish when you're happy";
function claimAction(res: any, err: any): { ok: boolean; clear: boolean; retry: boolean; toast: string | null; kind: string } {
  if (res && res.ok) return { ok: true, clear: true, retry: false, toast: CLAIM_READY, kind: "success" };
  const code = String((err && err.code) || ""), status = Number(err && err.status) || 0;
  const done = (toast: string, kind = "info") => ({ ok: false, clear: true, retry: false, toast, kind });
  if (code === "has_site") return done("You already have a website in Fence Works, so we kept yours — the preview we built wasn't copied over. Reply to Rob's email if you'd rather have the new one.", "info");
  if (code === "expired") return done("That website preview has ended — reply to Rob's email and he'll turn it back on.", "info");
  if (code === "claimed") return done("That website has already been claimed by another account.", "error");
  if (code === "invalid" || status === 400 || status === 404) return done("That website link isn't valid anymore — reply to Rob's email for a fresh one.", "error");
  if (code === "role") return done("Only the account owner can claim a website — ask them to open the link.", "error");
  if (status === 401) return { ok: false, clear: false, retry: false, toast: null, kind: "info" };   // signed out: try on the next sign-in
  return { ok: false, clear: false, retry: true, toast: null, kind: "info" };   // no workspace yet, offline, 5xx: keep it and retry
}

/* Claim the stashed Lead Engine site once. Resolves true when the site is
   theirs now. Never throws; keeps the token for next time on a transient failure. */
async function claimPendingSite(DB: any): Promise<boolean> {
  let tok: string | null = null;
  try { tok = localStorage.getItem("fw_pending_claim"); } catch (e) {}
  if (!tok || !DB || !DB.svcJson || !DB.apiBase || !DB.apiBase()) return false;
  for (let attempt = 0; attempt < 3; attempt++) {
    let res: any = null, err: any = null;
    try { res = await DB.svcJson("/leadengine-claim", "POST", { token: tok }); } catch (e) { err = e; }
    const a = claimAction(res, err);
    if (a.clear) { try { localStorage.removeItem("fw_pending_claim"); } catch (e) {} }
    if (!a.ok && a.toast) notify(a.toast, { kind: a.kind as any, ms: 10000 });
    if (!a.retry) return a.ok;
    await new Promise((r) => setTimeout(r, 2000 * (attempt + 1)));
  }
  return false;
}

const BLANK = { projects: [], quotes: [], activity: [], customProducts: [], customers: [], workOrders: [], settings: { markup: 2.0, laborRate: 28 }, companyReady: true };
function loadState() {
  try { const s = JSON.parse(localStorage.getItem(LS_KEY)); if (s) return Object.assign({ customProducts: [], customers: [], workOrders: [] }, s); } catch {}
  // first paint of a sandbox session: the seeded tenant (Demo Fence Co)
  if (DEMO.active) { try { return Object.assign(JSON.parse(JSON.stringify(BLANK)), DEMO.seed()); } catch (e) { /* fall through to blank */ } }
  return JSON.parse(JSON.stringify(BLANK));
}

/* match a customer record by email, else name (case-insensitive) */
function findCustomer(list, name, email) {
  const e = (email || "").trim().toLowerCase(), n = (name || "").trim().toLowerCase();
  return list.find(c => (e && (c.email || "").toLowerCase() === e) || (!e && n && (c.name || "").toLowerCase() === n));
}
function upsertCustomerInList(list, info) {
  if (!info || (!info.name && !info.email)) return { list, id: null };
  const existing = findCustomer(list, info.name, info.email);
  if (existing) {
    const merged = { ...existing,
      name: existing.name || info.name, phone: existing.phone || info.phone,
      email: existing.email || info.email, address: existing.address || info.address };
    return { list: list.map(c => c.id === existing.id ? merged : c), id: existing.id };
  }
  const id = newId("cust-");
  const rec = { id, name: info.name || info.email, phone: info.phone || "", email: info.email || "", address: info.address || "", notes: "", createdAt: Date.now() };
  return { list: [...list, rec], id };
}

/* next job number: max existing J-#### + 1 (never reuses a number after a
   delete the way a length-based counter did) */
function nextJobNumber(projects: any[]): string {
  const max = (projects || []).reduce((m: number, p: any) => {
    const hit = /^J-(\d+)$/.exec(String((p && p.job) || ""));
    return hit ? Math.max(m, parseInt(hit[1], 10)) : m;
  }, 1041);
  return "J-" + String(max + 1).padStart(4, "0");
}

/* rebuild the engine catalog: built-ins + custom (custom overrides same id) */
/* FW-002/003: every quote carries q.customerDoc — the customer-facing document
   (lines, subtotal, tax, total, footage, BOM quantities) precomputed here on
   the contractor's side, so the public hub never needs pricing inputs. At the
   moment a quote becomes a contract (signed / paid / e-signature stamped) the
   revision is frozen: signedTotal + signedDoc, replayed by computeQuoteView. */
const SIGNED_STATES = new Set(["signed", "paid"]);
function stampQuoteDoc(q, s) {
  if (!q) return q;
  const products = [...((s && s.customProducts) || []), ...window.VFP.PRODUCTS];
  const settings = (s && s.settings) || {};
  let next = q;
  try {
    const committed = SIGNED_STATES.has(next.status) || !!(next.signature && next.signature.at) || Number(next.depositPaid) > 0;
    if (committed && (next.signedTotal == null || !next.signedDoc)) {
      // freeze from the document as it stood BEFORE this write when we have one.
      // What freezes is the BASE (before change orders): the ledger adds the
      // approved COs on top of signedTotal, and an unsigned document's total
      // already carries them — freezing that counted each one twice.
      const before = q.customerDoc && q.customerDoc.quote ? q.customerDoc : null;
      const view = computeQuoteView({ ...next, signedTotal: next.signedTotal != null ? next.signedTotal : undefined, signedDoc: undefined }, settings, products);
      const beforeBase = before ? docBaseTotal(before, next.changeOrders) : null;
      const signedTotal = next.signedTotal != null ? Number(next.signedTotal) : (beforeBase != null ? beforeBase : Math.round((Number(view.quote.baseTotal) || 0) * 100) / 100);
      next = { ...next, signedTotal, signedDoc: next.signedDoc || (before && before.lines && before.lines.length ? { at: Date.now(), lines: before.lines, subtotal: before.quote.subtotal, tax: before.quote.tax, taxRate: before.quote.taxRate, taxLabel: before.quote.taxLabel, discountAmt: before.quote.discountAmt, discountLabel: before.quote.discountLabel, customerTotal: signedTotal } : signedDocOf(view)) };
    }
    next = { ...next, customerDoc: customerDocOf(next, settings, products) };
    // a won concrete quote freezes its priced blocks (revenue, cost, profit,
    // hours per job type) the way signedDoc freezes the lines: the reports read
    // this (lib/reports.concrete.ts; §13 item 17), so a rate card changed next
    // spring doesn't rewrite last year's margins
    if (committed && !next.wonSnapshot && verticalOf(next) === "concrete") {
      const v = computeQuoteView(next, settings, products);
      const pb = v && v.quote && v.quote.concrete ? v.quote.concrete.blocks || [] : [];
      const inputs = blocksOf(next.inputs && next.inputs.concrete);
      if (pb.length) next = { ...next, wonSnapshot: { at: Date.now(), blocks: pb.map((b: any, i: number) => ({ jobType: b.jobType, drop: b.jobType === "lift" ? ((inputs[i] && inputs[i].drop) || "unsure") : null, amount: Number(b.amount) || 0, cost: Number(b.cost) || 0, profit: Number(b.profit) || 0, laborHours: Number(b.laborHours) || 0 })) } };
    }
  } catch (e) { /* an engine hiccup never blocks the write; the doc is retried on the next one */ }
  return next;
}

function syncCatalog(customProducts) {
  const customIds = new Set(customProducts.map(p => p.id));
  window.VFP.PRODUCTS = [...BUILTIN_PRODUCTS.filter(p => !customIds.has(p.id)), ...customProducts];
}

/* fire an automation trigger into the backend service (fire-and-forget).
   No-op unless window.VFP_API_BASE is configured — preserves offline/demo use. */
/* Fire an automation trigger and REPORT WHAT HAPPENED. It used to swallow every
   outcome and return nothing, so a caller showing the customer a green "sent"
   badge had no way to know whether anything left the building — and the badge
   went up before the call was even made. It still never rejects (a telemetry
   failure must not break a store write), so the resolved value is the error
   channel: { ok, reason, detail }. Fire-and-forget callers can keep ignoring it. */
function emitAutomation(trigger, payload) {
  try {
    const DB = window.VFP_DB;
    if (!DB || !DB.emitEvent) return Promise.resolve({ ok: false, reason: "offline", detail: "Messaging isn't connected on this device." });
    return DB.emitEvent(trigger, payload).then(
      (r) => (r && r.skipped)
        ? { ok: false, reason: "not-configured", detail: "The automation service isn't set up for this workspace yet." }
        : { ok: true, result: r },
      (e) => ({ ok: false, reason: "failed", detail: String((e && e.message) || e) }),
    );
  } catch (e) {
    return Promise.resolve({ ok: false, reason: "failed", detail: String((e && e.message) || e) });
  }
}

/* When REMOTE state replaces this tab's (refused-stale save, realtime apply),
   remote is the truth — EXCEPT for records CREATED in this tab since it last
   hydrated: those exist nowhere else, and dropping them is exactly the
   "made an estimate, deployed, it's gone" data loss. Union them in by id
   (createdAt after the hydrate stamp, so a remote DELETE is never undone by
   an old tab re-adding it). Edits to pre-existing records still defer to
   remote — there's no per-field dirty log to merge with. */
function mergeRescuedState(remote, local, sinceTs) {
  const out = Object.assign(JSON.parse(JSON.stringify(BLANK)), remote);
  let rescued = 0;
  ["quotes", "customers", "workOrders", "projects"].forEach((k) => {
    const arr = Array.isArray(out[k]) ? out[k] : (out[k] = []);
    const have = new Set(arr.map((x) => x && x.id));
    (Array.isArray(local && local[k]) ? local[k] : []).forEach((x) => {
      if (x && x.id && !have.has(x.id) && Number(x.createdAt || 0) > sinceTs) { arr.push(x); rescued++; }
    });
  });
  // FW-007: a photo taken here with no signal (still pending upload) lives on
  // an EXISTING job, so the union above can't see it — put it back on its job
  const photos = pendingPhotosOf(local).filter((x) => Number(x.photo && x.photo.at) > sinceTs);
  if (photos.length) {
    const next = rescuePendingPhotos(out, photos, sinceTs);
    if (next !== out) { rescued += photos.length; return { state: next, rescued }; }
  }
  return { state: out, rescued };
}

function useStore() {
  const [state, setState] = React.useState<import("./types").AppState>(loadState);
  const [sync, setSync] = React.useState<any>(DEMO.active ? "demo" : (window.VFP_DB && window.VFP_DB.ready ? "loading" : "local"));
  const hydrated = React.useRef(false);
  const hydratedAt = React.useRef(Date.now());   // when this tab last took a full cloud state
  const remoteApply = React.useRef(false);
  const lastEditRef = React.useRef(0);
  const stateRef = React.useRef(state); stateRef.current = state;   // latest state for mount-time closures
  // set by importBatch / undoImport: the next push logs ONE activity event for
  // the whole batch instead of the diff's one-per-record (a 2,000-row import
  // would otherwise queue 2,000 tracking events against a 120/min limit)
  const bulkRef = React.useRef<any>(null);
  // plan caps: stateRef only catches up on render, so quotes / products added
  // since the last one are tallied here — a burst of adds can't slip past the
  // cap or be told "saved" when the updater then refuses them
  const addedRef = React.useRef<any>({ base: null, quotes: [], customProducts: [] });
  // the lens a NEW record is made under (lib/verticals.ts): App sets it every
  // render; fence stamps nothing, so a fence record is exactly what it was
  const lensRef = React.useRef<any>("fence");
  const capView = (key: "quotes" | "customProducts") => {
    if (addedRef.current.base !== stateRef.current) addedRef.current = { base: stateRef.current, quotes: [], customProducts: [] };
    const cur: any = stateRef.current;
    return { ...cur, [key]: [...(cur[key] || []), ...addedRef.current[key]] };
  };

  const quotaTold = React.useRef(false);
  React.useEffect(() => {
    // a full device (QuotaExceededError — usually job photos) must not crash
    // the render loop; the cloud copy still syncs, so say so once and move on
    try { localStorage.setItem(LS_KEY, JSON.stringify(state)); }
    catch (e) { if (!quotaTold.current) { quotaTold.current = true; notify("This device is out of storage — your work still syncs to the cloud. Remove some job photos.", { kind: "error", ms: 9000 }); } }
    // rolling device-local snapshots (45-min throttle inside) — the safety net
    // that survives a bad save, in cloud AND local-only mode
    try { const DB: any = window.VFP_DB; DB && DB.backups && DB.backups.auto(state); } catch (e) {}
  }, [state]);
  React.useLayoutEffect(() => { syncCatalog(state.customProducts || []); }, [state.customProducts]);
  // one-time backfill: quotes saved before customer documents existed get one
  // (and signed ones their frozen revision) the first time this workspace loads
  React.useEffect(() => {
    if (!(state.quotes || []).some((q: any) => q && !q.customerDoc)) return;
    setState((s: any) => ({ ...s, quotes: (s.quotes || []).map((q: any) => (q && !q.customerDoc ? stampQuoteDoc(q, s) : q)) }));
  }, [sync, (state.quotes || []).length]);
  // ensure catalog is synced on first render too
  React.useMemo(() => syncCatalog(state.customProducts || []), []);

  // ---- cloud: hydrate from Supabase on mount, then push changes ----
  React.useEffect(() => {
    const DB = window.VFP_DB;
    // sandbox: no cloud read, no realtime, no push — the store lives in this
    // tab only (lib/supabase also refuses every call while VFP_DEMO.active)
    if (DEMO.active) { hydrated.current = true; setSync("demo"); return; }
    if (!DB || !DB.ready) { setSync("local"); return; }
    const off = DB.onStatus((st, detail) => {
      // keep the last failure reason around for the sync pill's tooltip
      if (detail != null) (window as any).VFP_SYNC_DETAIL = String(detail);
      else if (st === "saved" || st === "idle") (window as any).VFP_SYNC_DETAIL = "";
      if (st === "stale" || st === "guard") {
        // a protective refusal: "stale" = this tab is behind the cloud
        // revision (open too long / old bundle); "guard" = this tab holds an
        // effectively-empty state and saving it would have ERASED a workspace
        // that has real data (fresh profile, failed boot read, DB outage).
        // The write was dropped — pull the truth. For "stale", MERGE instead
        // of replace: records created in this tab exist nowhere else, and
        // dropping them was the "made an estimate, deployed, it's gone" bug.
        DB.load().then((remote) => {
          if (!(remote && typeof remote === "object")) return;
          if (st === "stale") {
            // the refused write can carry EDITS to pre-existing records that the
            // merge below can't rescue (it only keeps new rows) — snapshot this
            // tab's state first so nothing typed here is unrecoverable
            try { DB.backups && DB.backups.take && DB.backups.take(stateRef.current, "stale-refused"); } catch (e) {}
            const { state: merged, rescued } = mergeRescuedState(remote, stateRef.current, hydratedAt.current);
            hydratedAt.current = Date.now();
            // rescued rows need a fresh save (this tab is rev-current after the
            // load above); nothing rescued → treat as a plain remote apply
            remoteApply.current = rescued === 0;
            try { DB.noteApplied && DB.noteApplied(merged); } catch (e) {}
            setState(() => merged);
            try {
              pushNotifs([{
                kind: "warn", route: null,
                text: rescued > 0
                  ? `⚠️ This tab was behind the cloud — merged the latest data and kept the ${rescued} record${rescued > 1 ? "s" : ""} created here. Edits made here to pre-existing records may need re-entry — a snapshot of this tab is under Settings → Backups ("stale-refused").`
                  : "⚠️ This tab was out of date — its save was blocked and the latest data was reloaded. A snapshot of this tab is under Settings → Backups (\"stale-refused\").",
              }]);
            } catch (e) {}
          } else {
            remoteApply.current = true;
            hydratedAt.current = Date.now();
            try { DB.noteApplied && DB.noteApplied(remote); } catch (e) {}
            setState(() => Object.assign(JSON.parse(JSON.stringify(BLANK)), remote));
            try {
              pushNotifs([{
                kind: "warn", route: null,
                text: "🛡️ Blocked a save that would have emptied your workspace — this tab had no data (bad boot or outage) while the cloud does. Latest data reloaded.",
              }]);
            } catch (e) {}
          }
        }).catch(() => {});
        setSync(st);
        return;
      }
      // FW-015 / L10: this tab's save landed, but the database judged it stale
      // on one or more estimates and kept ITS payment, signature and approved
      // change-order details over ours. That used to be a silent
      // last-write-wins over settled money. Reload the affected estimates so
      // the screen shows what is actually stored, and say so plainly.
      if (st === "conflict") {
        const hits = (DB.takeQuoteConflicts && DB.takeQuoteConflicts()) || [];
        DB.load().then((remote: any) => {
          if (!(remote && typeof remote === "object")) return;
          const byId = new Map((remote.quotes || []).map((q: any) => [q.id, q]));
          remoteApply.current = true;
          setState((s: any) => ({ ...s, quotes: (s.quotes || []).map((q: any) => byId.get(q.id) || q) }));
          try {
            const who = hits.map((h: any) => h.customer).filter(Boolean).join(", ");
            pushNotifs([{
              kind: "warn",
              route: hits.length === 1 && hits[0].id ? ["quote", { quoteId: hits[0].id }] : null,
              text: `🔒 ${hits.length > 1 ? `${hits.length} estimates` : who ? `${who}'s estimate` : "An estimate"} changed on the server while this tab was open — their payment, signature and approved change orders were kept, and the latest copy is reloaded. Check anything you edited here.`,
            }]);
          } catch (e) {}
        }).catch(() => {});
        setSync("saved");
        return;
      }
      // "error" after hydration = a SAVE failed (reads were fine); before
      // hydration = the boot READ failed and the tab is read-only. Different
      // pills — one asks you to hover for what didn't save, one offers retry.
      setSync(st === "saved" ? "saved" : st === "saving" ? "saving" : st === "error" ? (hydrated.current ? "saveerr" : "error") : st === "loading" ? "loading" : st === "nosession" ? "nosession" : "idle");
    });
    let retryTimer: any = null;
    const applyRemote = (remote: any) => {
      if (remote && typeof remote === "object") {
        // about to replace a data-bearing local state with an EMPTY cloud one
        // (outage aftermath / wiped workspace)? Pin the local copy as a labeled
        // snapshot FIRST — it may be the last one anywhere.
        try {
          const cur = stateRef.current || ({} as any);
          const curHas = ((cur.quotes || []).length + (cur.customers || []).length + (cur.workOrders || []).length) > 0;
          const remoteEmpty = !(((remote.quotes || []).length + (remote.customers || []).length + (remote.workOrders || []).length) > 0);
          if (curHas && remoteEmpty && DB.backups) DB.backups.take(cur, "pre-empty-cloud");
        } catch (e) {}
        // BOOT RESCUE: records created on this device AFTER the last save that
        // fully landed exist nowhere but localStorage (tab closed inside the
        // save debounce, session expired overnight, saves erroring) — the old
        // wholesale replace here silently dropped them, which read as "made an
        // estimate, deployed/reloaded, it's gone". Union them back in by id.
        // No stamp yet (first boot on this device) → no rescue: without a
        // last-good-sync time, "local-only" can't be told apart from "deleted
        // on another device", and resurrecting deletes is the worse failure.
        let next = remote, rescued = 0;
        try {
          const syncedAt = Number(localStorage.getItem("fw_last_synced_at")) || 0;
          if (syncedAt > 0) { const m = mergeRescuedState(remote, stateRef.current, syncedAt); next = m.state; rescued = m.rescued; }
        } catch (e) {}
        remoteApply.current = rescued === 0;   // rescued rows ride out on a fresh save
        try { DB.noteApplied && DB.noteApplied(next); } catch (e) {}
        setState(() => Object.assign(JSON.parse(JSON.stringify(BLANK)), next));
        if (rescued > 0) {
          try {
            pushNotifs([{ kind: "warn", route: null,
              text: `🛟 Recovered ${rescued} record${rescued > 1 ? "s" : ""} from this device that hadn't reached the cloud — re-saving now.` }]);
          } catch (e) {}
        }
      }
      hydrated.current = true;
      hydratedAt.current = Date.now();
      setSync("idle");
    };
    const hydrate = () => DB.load().then(applyRemote).catch(() => {
      // couldn't READ the cloud → this tab must not WRITE it. hydrated stays
      // false, which blocks every push: syncing whatever half-state this tab
      // holds (often a blank fresh profile) over the real workspace is exactly
      // how workspaces get wiped. Stay read-only and retry until a load lands.
      setSync("error");
      retryTimer = setTimeout(hydrate, 10000);
    });
    hydrate();
    // regained network / tab refocused / pill clicked while the boot read is
    // still failing → retry NOW instead of waiting out the 10s loop. Only for
    // the un-hydrated case: a hydrated tab re-pulling here could eat unsaved
    // local edits, and its saves already retry on their own.
    const kickRetry = () => {
      if (hydrated.current) return;
      if (retryTimer) clearTimeout(retryTimer);
      hydrate();
    };
    window.addEventListener("online", kickRetry);
    window.addEventListener("focus", kickRetry);
    window.addEventListener("fw:retry-sync", kickRetry);
    // realtime updates from another device — union-merged so a remote refresh
    // never eats records created here that haven't landed in the cloud yet
    const unsub = DB.subscribe((remote) => {
      if (Date.now() - lastEditRef.current < 8000) return;   // ignore echoes right after a local edit
      if (remote && typeof remote === "object") {
        const { state: merged, rescued } = mergeRescuedState(remote, stateRef.current, hydratedAt.current);
        remoteApply.current = rescued === 0;   // rescued rows ride out on a save
        try { DB.noteApplied && DB.noteApplied(merged); } catch (e) {}
        setState(() => merged);
      }
    });
    return () => {
      off && off(); unsub && unsub(); if (retryTimer) clearTimeout(retryTimer);
      window.removeEventListener("online", kickRetry);
      window.removeEventListener("focus", kickRetry);
      window.removeEventListener("fw:retry-sync", kickRetry);
    };
  }, []);

  // push state to cloud after hydration (skip the echo from a remote apply)
  const trackPrev = React.useRef<any>(null);   // last state the activity diff saw
  React.useEffect(() => {
    const bulk = bulkRef.current; bulkRef.current = null;
    if (DEMO.active) { DEMO.touch(); return; }   // activity extends the sandbox; nothing is saved
    const DB = window.VFP_DB;
    const prev = trackPrev.current; trackPrev.current = state;
    if (!DB || !DB.ready || !hydrated.current) return;
    if (remoteApply.current) { remoteApply.current = false; return; }
    lastEditRef.current = Date.now();   // mark a genuine local edit
    if (bulk) track(bulk.e, bulk.p);    // an import / undo: one event for the batch
    else trackStateChange(prev, state); // quote sent, product added… → FW Admin timeline (lib/track.ts)
    DB.save(state);
  }, [state]);

  // ---- FW-007: the crew's offline outbox (lib/fieldsync.ts) ----------------
  // Crew actions queue on this device until the service takes them. Whatever
  // replaces the jobs here — the boot read, a realtime refresh, a stale-tab
  // reload — the ones still on their way are applied again on top, so a
  // clock-in made with no signal never vanishes from the screen.
  const fieldVer = useOutboxVersion();
  React.useLayoutEffect(() => {
    if (fieldView(state) !== state) setState((s: any) => fieldView(s));
  }, [state, fieldVer]);
  React.useEffect(() => {
    if (DEMO.active) return;
    return startFieldSync({
      setState,
      hydrated: () => hydrated.current,
      // a job's queue drained: its held-back row goes out now
      save: () => { const DB: any = window.VFP_DB; if (DB && DB.ready && hydrated.current) DB.save(stateRef.current); },
      notify: (m: string, o?: any) => notify(m, o),
    });
  }, []);

  // ---- deploy watchdog ----------------------------------------------------
  // A tab still running the PREVIOUS bundle after a deploy is the #1 source of
  // stale-save refusals (and the data pain that comes with them). Compare the
  // hashed bundle path in the live index.html against the one this tab booted
  // with; on a mismatch, flush + refresh the moment the tab is hidden — or
  // tell the operator if it stays foreground.
  React.useEffect(() => {
    const scriptEl = document.querySelector('script[type="module"][src*="/assets/"]');
    const cur = scriptEl && scriptEl.getAttribute("src");
    if (!cur) return;   // dev server — no hashed bundle to compare
    const norm = (s: any) => String(s || "").replace(location.origin, "");
    let newBuild = false, told = false;
    const refresh = () => {
      try { const DB: any = window.VFP_DB; Promise.resolve(DB && DB.flushNow && DB.flushNow()).finally(() => location.reload()); }
      catch (e) { location.reload(); }
    };
    const check = async () => {
      if (newBuild) return;
      try {
        const r = await fetch("/index.html", { cache: "no-store" });
        if (!r.ok) return;
        const m = (await r.text()).match(/src="([^"]*\/assets\/index-[^"]+\.js)"/);
        if (!(m && m[1]) || norm(m[1]) === norm(cur)) return;
        newBuild = true;
        if (document.hidden) { refresh(); return; }
        if (!told) {
          told = true;
          try { pushNotifs([{ kind: "warn", text: "🚀 A new version was deployed — this tab refreshes automatically when you switch away (your work is saved first).", route: null }]); } catch (e) {}
        }
      } catch (e) { /* offline / transient — try again next tick */ }
    };
    const onVis = () => { if (newBuild && document.hidden) refresh(); };
    const iv = setInterval(check, 5 * 60 * 1000);
    const kick = setTimeout(check, 30000);
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); clearTimeout(kick); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  const api = React.useMemo<import("./types").StoreApi>(() => ({
    setLens: (id) => { lensRef.current = id; },
    addProject: (p0) => setState(s => {
      const p = stampVertical(p0, lensRef.current);
      const { list, id } = upsertCustomerInList(s.customers || [], { name: p.customer, address: p.address });
      return { ...s, customers: list, projects: [...s.projects.filter(x => x.id !== p.id), { ...p, customerId: p.customerId || id }] };
    }),
    deleteProject: (id) => setState(s => ({ ...s, projects: (s.projects || []).filter(p => p.id !== id) })),
    updateProject: (id, patch) => setState(s => ({ ...s, projects: (s.projects || []).map(p => p.id === id ? { ...p, ...patch } : p) })),
    // plan cap (lib/limits.ts): a new estimate past this month's allowance is
    // refused here as well as in the UI, so no path gets round it. Inbound
    // homeowner requests (widget / booking) are never refused.
    addQuote: (q0) => {
      const q = stampVertical(q0, lensRef.current);
      const fits = (s: any) => canAddQuote(s.quotes, q, quoteLimit(planOfState(s)), Date.now());
      if (!fits(capView("quotes"))) { notify("Monthly estimate limit reached on your plan — upgrade in Account & Billing to create more.", { kind: "error" }); return false; }
      addedRef.current.quotes.push(q);
      setState(s => {
        if (!fits(s)) return s;
        const { list, id } = upsertCustomerInList(s.customers || [], { name: q.customer, phone: q.phone, email: q.email, address: q.address });
        // fire the automation trigger (financing pre-qual + quote follow-up sequence).
        // no-op unless window.VFP_API_BASE points at the automation service.
        emitAutomation("quote.created", { customer: { id: q.customerId || id, name: q.customer, phone: q.phone, email: q.email }, job: { amount: Number(q.total) || 0 } });
        // EST-02: a new quote is a DRAFT. This used to seed sends/lastSentAt at
        // creation, so every estimate was born "sent" — it entered the follow-up
        // chase, counted as a touch, and dated its own send from createdAt. A
        // send is logged by logQuoteSend when one actually happens.
        return { ...s, customers: list, quotes: [...s.quotes, stampQuoteDoc(withQuoteDefaults({ ...q, customerId: q.customerId || id }, s.settings), s)] };
      });
      return true;
    },
    // stamp wonAt the first time a quote turns signed/paid — commission payouts
    // and "won this month" math need the WIN date, not the created date.
    updateQuote: (id, patch) => setState(s => ({ ...s, quotes: s.quotes.map(q => {
      if (q.id !== id) return q;
      const next = { ...q, ...patch };
      if ((patch.status === "signed" || patch.status === "paid") && !q.wonAt && !next.wonAt) next.wonAt = Date.now();
      return stampQuoteDoc(next, s);
    }) })),
    // the server just wrote this quote (an in-person signature or approval) and
    // handed back the row as it now stands: take it WHOLE, row version and all,
    // rather than merging — merging would keep keys only this tab ever had, and
    // saving our older copy over the signature is what this exists to prevent
    adoptQuote: (fresh) => setState(s => ({ ...s, quotes: s.quotes.map(q => {
      if (!fresh || q.id !== fresh.id) return q;
      const next = { ...fresh };
      if ((next.status === "signed" || next.status === "paid") && !next.wonAt) next.wonAt = q.wonAt || Date.now();
      return stampQuoteDoc(next, s);
    }) })),
    deleteQuote: (id) => setState(s => ({ ...s, quotes: (s.quotes || []).filter(q => q.id !== id) })),
    // record another send on an existing quote (keeps one row + a history)
    // `via` marks a send this build actually logged, so a future reader can tell
    // a real send from one of the seeded entries older records still carry
    logQuoteSend: (id, via) => setState(s => ({ ...s, quotes: s.quotes.map(q => q.id === id ? { ...q, sends: [...(q.sends || []), { at: Date.now(), via: via || "link" }], lastSentAt: Date.now() } : q) })),
    // ---- customers ----
    addCustomer: (c) => setState(s => ({ ...s, customers: [...(s.customers || []), { id: "cust-" + Date.now(), createdAt: Date.now(), notes: "", ...c }] })),
    updateCustomer: (id, patch) => setState(s => ({ ...s, customers: (s.customers || []).map(c => c.id === id ? { ...c, ...patch } : c) })),
    // deleting a customer keeps their quotes / jobs / work orders: the link is
    // cut (customerId → null) but the denormalized name / phone / email stays
    // on each record (filled onto the quote from the customer if it was blank)
    // so nothing on the books turns anonymous.
    deleteCustomer: (id) => setState(s => {
      const cust = (s.customers || []).find(c => c.id === id);
      const unlink = (x: any) => x.customerId === id ? { ...x, customerId: null, customer: x.customer || (cust && cust.name) || "" } : x;
      const quotes = (s.quotes || []).map((q: any) => q.customerId === id
        ? { ...unlink(q), phone: q.phone || (cust && cust.phone) || "", email: q.email || (cust && cust.email) || "", address: q.address || (cust && cust.address) || "" }
        : q);
      return { ...s, customers: (s.customers || []).filter(c => c.id !== id), quotes,
        projects: (s.projects || []).map(unlink), workOrders: (s.workOrders || []).map(unlink) };
    }),
    // (re)link a quote to the customer it names — upserts the customer record
    // by name/email, so regenerating an estimate under a different name lands
    // on the right person instead of leaving the quote unlinked.
    linkQuoteCustomer: (quoteId, info) => setState(s => {
      const { list, id: cid } = upsertCustomerInList(s.customers || [], info);
      if (!cid) return s;
      return { ...s, customers: list, quotes: (s.quotes || []).map((q: any) => q.id === quoteId ? { ...q, customerId: cid } : q) };
    }),
    // Merge two customer records into one: `keep` survives, `drop` is removed.
    // Re-points every quote/project/workOrder off the dropped customer to the
    // survivor (sets customerId), fills blank contact fields on the survivor
    // from the dropped record, and concatenates the CRM arrays/notes — so no
    // job is orphaned and nothing on either record is lost. Pure state.
    mergeCustomers: (keepId, dropId) => setState(s => {
      if (keepId === dropId) return s;
      const list = s.customers || [];
      const keep = list.find(c => c.id === keepId);
      const drop = list.find(c => c.id === dropId);
      if (!keep || !drop) return s;
      // a record belongs to the dropped customer if it's linked by id, OR (legacy,
      // unlinked) it matches the dropped customer's name — same rule the CRM uses
      // in jobsForCustomer, so the survivor inherits exactly what showed under it.
      const dropName = (drop.name || "").trim().toLowerCase();
      const ownedByDrop = (x) => x.customerId === dropId || (!x.customerId && dropName && (x.customer || "").trim().toLowerCase() === dropName);
      const repoint = (arr) => (arr || []).map(x => ownedByDrop(x) ? { ...x, customerId: keepId } : x);
      const quotes = repoint(s.quotes);
      const projects = repoint(s.projects);
      // work orders link by customerId or via a quote that belonged to the drop
      const dropQuoteIds = new Set((s.quotes || []).filter(ownedByDrop).map(q => q.id));
      const workOrders = (s.workOrders || []).map(w =>
        (w.customerId === dropId || (w.quoteId && dropQuoteIds.has(w.quoteId))) ? { ...w, customerId: keepId } : w);
      // union contact fields onto the survivor (only fill blanks — keep wins)
      const merged: any = { ...keep };
      for (const k of ["name", "phone", "email", "address", "street", "city", "state", "zip", "gateCode", "pets", "hubToken"]) {
        if (!merged[k] && drop[k]) merged[k] = drop[k];
      }
      // notes: concatenate the free-text site notes; arrays: append the dropped one's
      const noteA = (keep.notes || "").trim(), noteB = (drop.notes || "").trim();
      merged.notes = noteA && noteB ? noteA + "\n\n" + noteB : (noteA || noteB);
      merged.noteLog = [...(keep.noteLog || []), ...(drop.noteLog || [])];
      merged.tasks = [...(keep.tasks || []), ...(drop.tasks || [])];
      merged.files = [...(keep.files || []), ...(drop.files || [])];
      // tags: union both records' tags (dedup, case-insensitive) so neither is lost
      const tagUnion = [...(keep.tags || []), ...(drop.tags || [])];
      const seenTags = new Set<string>();
      merged.tags = tagUnion.filter((t: string) => { const k = (t || "").trim().toLowerCase(); if (!k || seenTags.has(k)) return false; seenTags.add(k); return true; });
      const customers = list.filter(c => c.id !== dropId).map(c => c.id === keepId ? merged : c);
      return { ...s, customers, quotes, projects, workOrders };
    }),
    // ---- work orders ----
    addWorkOrder: (wo0) => setState(s => {
      // a job made from a project is the project's service; a loose job is the lens's
      const parent = wo0 && wo0.projectId ? (s.projects || []).find((p: any) => p && p.id === wo0.projectId) : null;
      const wo = parent && parent.vertical && !(wo0 && wo0.vertical) ? { ...wo0, vertical: parent.vertical } : stampVertical(wo0, lensRef.current);
      return { ...s, workOrders: [...(s.workOrders || []), { id: newId("wo-"), createdAt: Date.now(), status: "unscheduled", ...wo }] };
    }),
    updateWorkOrder: (id, patch) => setState(s => {
      const w = (s.workOrders || []).find(x => x.id === id);
      // enrich the customer payload with phone/email from the customer record so
      // the automations actually have somewhere to send.
      const custOf = (w) => { const c: any = (s.customers || []).find(x => x.id === w.customerId) || {}; return { id: w.customerId, name: w.customer || c.name, address: w.address || c.address, phone: c.phone, email: c.email }; };
      const extra: any = {};   // other arrays a job change touches (the customer's care plans, the quote's due date)
      if (w && patch && patch.status) {
        const wasDone = /^(done|completed)$/.test(w.status || "");
        if (/^(done|completed)$/.test(patch.status) && !wasDone) {
          const doneAt = Number(patch.completedAt || w.completedAt) || Date.now();
          const doneYmd = localYmd(new Date(doneAt));
          // a finished concrete job starts the customer's care plans — a re-seal
          // every two years, re-caulk and cleaning yearly (lib/careplans.ts) —
          // or, when it WAS a plan's rebook, rolls that plan forward (§13 item 15)
          if (verticalOf(w) === "concrete" && w.customerId) {
            extra.customers = (s.customers || []).map((c: any) => {
              if (!c || c.id !== w.customerId) return c;
              const rebook = (c.carePlans || []).some((p: any) => p && p.lastQuoteId && p.lastQuoteId === w.quoteId);
              const rolled = rollPlansFor(c.carePlans, w.quoteId, doneYmd);
              const fresh = rebook || (w as any).kind === "warranty" ? [] : plansFromJob(w, doneYmd);
              const next = mergePlans(rolled, fresh);
              return next.length || (Array.isArray(c.carePlans) && c.carePlans.length) ? { ...c, carePlans: next } : c;
            });
          }
          // a pay-on-completion quote (its own deposit is 0, nothing collected): the
          // balance falls due tomorrow — the dunning clock the service runs
          // (server/src/scheduler.js overdueNotice) starts here, not at signing (§13 item 16)
          if (w.quoteId) extra.quotes = (s.quotes || []).map((q: any) => {
            if (!q || q.id !== w.quoteId || q.dueDate || !(q.depositPct != null && Number(q.depositPct) === 0)) return q;
            const d = new Date(doneAt); d.setDate(d.getDate() + 1);
            return { ...q, dueDate: localYmd(d) };
          });
        }
        // stamp completedAt on the first completion — the balance auto-chase
        // cadence (day 1 / 7 / 14) and warranty clock both key off it
        if (/^(done|completed)$/.test(patch.status) && !wasDone && !w.completedAt) patch = { ...patch, completedAt: Date.now() };
        // …and the warranty terms the job carries (wo.cx.warranty) are dated from that day, as the crew's job.complete op does (lib/outbox.ts)
        if (/^(done|completed)$/.test(patch.status) && !wasDone && !Array.isArray((w as any).warranty) && (w as any).cx && Array.isArray((w as any).cx.warranty) && (w as any).cx.warranty.length) patch = { ...patch, warranty: datedWarranty((w as any).cx.warranty, patch.completedAt || w.completedAt) };
        // a warranty re-visit's completion asks for no review, posts nothing and cross-sells nothing — the original job did
        if (/^(done|completed)$/.test(patch.status) && !wasDone && (w as any).kind !== "warranty") {
          // completion → review request / interceptor, photos, farming, social
          // auto-post + sequences. Tagged before/after shots travel with the
          // event (befores first) so the post leads with the transformation;
          // nothing tagged → all job photos go. Capped — data-URL thumbnails
          // ride a 2 MB request limit.
          const ph = ((w as any).photos || []).filter((p: any) => p && p.url);
          const tagged = [...ph.filter((p: any) => p.tag === "before"), ...ph.filter((p: any) => p.tag === "after")];
          const photos = (tagged.length ? tagged : ph).slice(0, 8).map((p: any) => ({ url: p.url, tag: p.tag || null }));
          // what the crew ticked for the other service (lib/outbox.ts CROSS_SELL_CHECKS) rides along for the cross-sell rules
          const fc = (w as any).fieldChecklist || {};
          const noted = { concrete: !!(fc.xs_concrete && fc.xs_concrete.done), fence: !!(fc.xs_fence && fc.xs_fence.done) };
          emitAutomation("job.completed", { customer: custOf(w), job: { id: w.projectId || w.id, address: w.address, title: w.title || null, photos, vertical: verticalOf(w), jobTypes: (w as any).jobTypes || [], noted } });
        } else if (patch.status === "in_progress" && w.status !== "in_progress") {
          // Work has STARTED. This is not the same moment as "on the way" —
          // that one is the crew tapping On my way before they leave, and it
          // emits crew.enroute from the job / calendar. Firing enroute here
          // meant the customer got "we're on the way" after the crew had
          // already arrived and started. Both are events of the one
          // job_status automation, each independently switchable.
          emitAutomation("job.started", { customer: custOf(w), job: { id: w.projectId || w.id, address: w.address, vertical: verticalOf(w) } });
        }
      }
      // a booked install that MOVES → let the customer know it slipped
      if (w && patch && patch.scheduledStart !== undefined && w.scheduledStart
          && patch.scheduledStart !== w.scheduledStart
          && !/^(done|completed|cancelled)$/i.test(patch.status || w.status || "")) {
        emitAutomation("job.delayed", {
          customer: custOf(w),
          job: { id: w.projectId || w.id, date: patch.scheduledStart, previousDate: w.scheduledStart, vertical: verticalOf(w) },
        });
      }
      // the job timeline: stamp the moments that have no timestamp of their
      // own (status flips, supplies ordered, schedule set, readiness checks)
      if (w && patch) {
        const now = Date.now();
        const hist = [];
        if (patch.status && patch.status !== w.status) hist.push({ kind: "status", at: now, from: w.status || null, to: patch.status });
        if (patch.suppliesOrdered === true && !w.suppliesOrdered) hist.push({ kind: "supplies", at: now, eta: patch.materialEta || w.materialEta || null });
        if (patch.scheduledStart !== undefined && patch.scheduledStart !== w.scheduledStart) hist.push({ kind: "scheduled", at: now, start: patch.scheduledStart || null, end: patch.scheduledEnd !== undefined ? patch.scheduledEnd : w.scheduledEnd || null, crewId: patch.crewId !== undefined ? patch.crewId : w.crewId || null, ...(patch.moveReason ? { reason: String(patch.moveReason) } : {}) });
        else if (patch.crewId !== undefined && patch.crewId !== w.crewId && (patch.scheduledStart || w.scheduledStart)) hist.push({ kind: "scheduled", at: now, start: patch.scheduledStart || w.scheduledStart, end: patch.scheduledEnd !== undefined ? patch.scheduledEnd : w.scheduledEnd || null, crewId: patch.crewId || null });
        if (patch.readiness && typeof patch.readiness === "object") {
          const r0: any = w.readiness || {}, r1: any = patch.readiness;
          const LABEL: any = { siteVisit: "Site visit confirmed", permit: "Permit pulled",
            // the concrete job's checks (lib/concrete.steps.ts CONCRETE_READINESS)
            cx_access: "Rig access confirmed", cx_weather: "Weather window confirmed", cx_prep: "Customer prep told", cx_heat: "In-floor heat & utilities in the slab checked", cx_moisture: "Moisture test recorded", cx_cleared: "Floor cleared", cx_cure: "Cure times told to the customer" };
          Object.keys(LABEL).forEach((k) => { if (r1[k] && !r0[k]) hist.push({ kind: "readiness", at: now, text: LABEL[k] }); });
        }
        if (hist.length) patch = { ...patch, history: [...(Array.isArray(w.history) ? w.history : []), ...hist].slice(-200) };
        // the move's reason lives in the history, not on the job; a moved job
        // sheds the overnight weather flag the service stamped for the old day (§13 item 13)
        if (patch.moveReason !== undefined) { const { moveReason, ...rest } = patch; patch = rest; }
        if (patch.scheduledStart !== undefined && patch.scheduledStart !== w.scheduledStart && (w as any).weatherFlag) patch = { ...patch, weatherFlag: null };
      }
      return { ...s, ...extra, workOrders: (s.workOrders || []).map(w => w.id === id ? { ...w, ...patch } : w) };
    }),
    deleteWorkOrder: (id) => setState(s => ({ ...s, workOrders: (s.workOrders || []).filter(w => w.id !== id) })),
    // FW-007: a crew field action (clock, break, on my way, start, done, note,
    // checklist) — applied here at once and queued in the offline outbox, which
    // the service applies in order, once (lib/fieldsync.ts, lib/outbox.ts)
    fieldOp: (op) => runFieldOp(op, setState),
    // the job as the service just stored it (an office answer to a held crew
    // update): taken whole, with no automation side effects
    adoptWorkOrder: (w) => setState(s => (w && w.id ? { ...s, workOrders: (s.workOrders || []).map(x => x.id === w.id ? w : x) } : s)),
    addActivity: (text) => setState(s => ({ ...s, activity: [{ text, time: "Just now" }, ...s.activity].slice(0, 20) })),
    setCompany: (patch) => setState(s => ({ ...s, company: { ...(s.company || {}), ...patch } })),
    addMessage: (threadId, msg) => setState(s => { const th = s.threads || {}; return { ...s, threads: { ...th, [threadId]: [...(th[threadId] || []), msg] } }; }),
    setSettings: (patch) => setState(s => ({ ...s, settings: { ...s.settings, ...patch } })),
    // plan cap (lib/limits.ts): a NEW product past the plan's limit is refused
    // here too (editor, duplicate, a stale tab); an id already in the catalog
    // is an edit and always saves, over the cap or not
    saveProduct: (product0, isNew) => {
      const product = isNew ? stampVertical(product0, lensRef.current) : product0;   // an edit keeps the service it has
      const fits = (s: any) => canSaveProduct(s.customProducts, BUILTIN_IDS, product, planLimit(planOfState(s), "products"));
      const view = capView("customProducts");
      if (!fits(view)) { notify("Product limit reached on your plan — upgrade in Account & Billing to add more.", { kind: "error" }); return false; }
      if (!view.customProducts.some((p: any) => p && p.id === product.id)) addedRef.current.customProducts.push(product);
      setState(s => {
        if (!fits(s)) return s;
        const others = (s.customProducts || []).filter(p => p.id !== product.id);
        const next = [...others, product];
        syncCatalog(next);
        return { ...s, customProducts: next };
      });
      return true;
    },
    deleteProduct: (id) => setState(s => {
      const next = (s.customProducts || []).filter(p => p.id !== id);
      syncCatalog(next);
      return { ...s, customProducts: next };
    }),
    addProjectFromQuote: (q, quote) => setState(s => {
      if (s.projects.some(p => p.fromQuote === q.id)) return s;
      const job = nextJobNumber(s.projects);
      const { list, id: custId } = upsertCustomerInList(s.customers || [], { name: q.customer, phone: q.phone, email: q.email, address: q.address });
      const pid = newId("p");
      // seed the work order's crew hours from the estimate's labor model so the
      // schedule sizes the job before anyone types a number
      let crewHours: any = null;
      try { const h = computeQuoteView(q, s.settings).quote.laborHours; crewHours = isFinite(h) && h > 0 ? Math.round(h * 2) / 2 : null; } catch (e) { crewHours = null; }
      // a concrete job carries its own cost-free scope, the estimate as it was
      // (foam, holes, hours, supplies) and the warranty terms the rate cards
      // give it: a crew login has no quote and no cards, and the cards change
      // (lib/concrete.actuals.ts — the lift log, recipe learning, the warranty)
      let cx: any = null;
      if (verticalOf(q) === "concrete") {
        try {
          const view: any = computeQuoteView(q, s.settings).quote;
          const cards: any = rateCardsOf(s.settings);
          const blocks = blocksOf(q.inputs && q.inputs.concrete);
          const lift = blocks.filter((b: any) => b.jobType === "lift" && cards.lift).map((b: any) => measuresOf(b, cards.lift));
          const mat = Array.isArray(view.mat) ? view.mat : [];
          cx = {
            blocks: blocks.map((b: any) => ({ jobType: b.jobType, sqft: b.sqft, ft: b.ft, sections: b.sections, drop: b.drop, surfaces: b.surfaces, crackFt: b.crackFt, system: b.system, condition: b.condition })),
            slabs: ((q.inputs && q.inputs.concrete && q.inputs.concrete.slabs) || []).map((sl: any) => ({ id: sl.id, kind: sl.kind, jobType: sl.jobType, label: sl.label || sl.kind, sqft: sl.sqft, sections: sl.sections })),
            est: {
              hours: Number(view.laborHours) || 0,
              foamLb: Math.round(mat.filter((m: any) => /foam/i.test(String(m.label)) && /lb/i.test(String(m.uom))).reduce((a: number, m: any) => a + (Number(m.qty) || 0), 0) * 10) / 10,
              holes: lift.reduce((a: number, m: any) => a + (Number(m.hole) || 0), 0),
              sections: lift.reduce((a: number, m: any) => a + (Number(m.section) || 0), 0),
              sqftInch: Math.round(lift.reduce((a: number, m: any) => a + (Number(m["sqft-inch"]) || 0), 0)),
              // a lift priced per lb of foam or per cu ft of void (§13 item 28): the quantity, the rate and the minimum the quote was priced on, so the job settles by its log without the card
              basis: (() => {
                const bl = ((view.concrete && view.concrete.blocks) || []).filter((p: any) => p && p.jobType === "lift" && p.basis);
                if (!bl.length || !bl.every((p: any) => p.basis.method === bl[0].basis.method)) return null;
                return { method: bl[0].basis.method, unit: bl[0].basis.unit, rate: Number(bl[0].basis.rate) || 0, qty: Math.round(bl.reduce((a: number, p: any) => a + (Number(p.basis.qty) || 0), 0) * 100) / 100, minimum: Number(cards.lift && cards.lift.minimum) || 0 };
              })(),
              // the pack each supply is bought in rides along (no dollars): the foam draw at completion counts sets, not pounds (§13 item 18)
              supplies: mat.map((m: any) => ({ label: m.label, qty: m.qty, uom: m.uom, ...(m.sku ? { sku: m.sku } : {}), ...(m.packs != null ? { packs: m.packs, packSize: m.packSize || null, packLabel: m.packLabel || null } : {}) })),
            },
            warranty: warrantyTermsFor((q as any).jobTypes, cards),
          };
        } catch (e) { cx = null; }
      }
      const project = { id: pid, fromQuote: q.id, job, customer: q.customer, address: q.address,
        customerId: custId, lf: q.lf, total: q.total, productName: q.productName, productId: q.productId,
        createdAt: Date.now(), status: "won", snapshot: q.inputs };
      const wo = { id: "wo-" + pid, projectId: pid, quoteId: q.id, customerId: custId,
        customer: q.customer, address: q.address, title: (q.customer || "Job") + " \u2014 " + (q.productName || VERTICALS[verticalOf(q)].label),
        total: q.total, lf: q.lf, crewId: s.settings && s.settings.activeCrewId, crewHours,
        // a concrete job's types ride on the work order: the weather rules and the crew checklist read them without the quote
        ...(verticalOf(q) === "concrete" && Array.isArray((q as any).jobTypes) ? { jobTypes: (q as any).jobTypes } : {}),
        ...(cx ? { cx } : {}),
        scheduledStart: "", scheduledEnd: "", status: "materials", suppliesOrdered: false, materialEta: "", priority: false, notes: "", createdAt: Date.now() };
      // job won (deposit paid \u2192 project + work order): unlocks the upsell, 811/
      // diggers locate, permit-check, and crew-dispatch automations.
      const cust = { id: custId, name: q.customer, phone: q.phone, email: q.email, address: q.address };
      emitAutomation("job.booked", { customer: cust, job: { id: pid, amount: Number(q.total) || 0, address: q.address, vertical: verticalOf(q) } });
      emitAutomation("workorder.created", { customer: cust, job: { id: pid, vertical: verticalOf(q) }, workorder: { id: wo.id, total: q.total, vertical: verticalOf(q) } });
      // the won job and its work order are the quote's service (nothing is stamped for fence)
      return { ...s, customers: list, projects: [...s.projects, stampVertical(project, verticalOf(q))], workOrders: [...(s.workOrders || []), stampVertical(wo, verticalOf(q))] };
    }),
    // ---- Import & export (dataio.tsx) ----
    // A whole import is ONE state write: one localStorage write, one cloud
    // save, one activity event. Nothing here goes through updateWorkOrder /
    // addQuote / addProjectFromQuote, so no automation is emitted — an
    // imported completed job never sends a review request or a "your fence
    // is finished" text. The pure halves live in lib/dataio.ts.
    importBatch: (batch, meta) => {
      bulkRef.current = { e: "data.imported", p: { kind: batch && batch.kind, n: ((batch && batch.customers) || []).length + ((batch && batch.workOrders) || []).length + ((batch && batch.products) || []).length } };
      setState(s => {
        const next = applyImportBatch(s, batch, meta);
        if (next.customProducts !== s.customProducts) syncCatalog(next.customProducts || []);
        return next;
      });
    },
    // take an import back; opts.max caps how many records of each kind one
    // call removes (the cloud prune rides the URL — dataio.tsx slices it)
    undoImport: (batchId, opts) => {
      bulkRef.current = { e: "data.import_undone", p: {} };
      setState(s => {
        const next = revertImportBatch(s, batchId, opts);
        syncCatalog(next.customProducts || []);
        return next;
      });
    },
    // wholesale replacement — the backup-restore path. Runs through setState
    // like any edit, so the cloud push + localStorage mirror happen normally.
    replaceState: (next) => setState(() => Object.assign(JSON.parse(JSON.stringify(BLANK)), next)),
    reset: () => { localStorage.removeItem(LS_KEY); setState(loadState()); },
  }), []);
  return [state, api, sync];
}

const TOOLS = [
  { id: "estimator", label: "Estimator", icon: "map" },
  { id: "reports", label: "Reports", icon: "activity" },
  { id: "calendar", label: "Schedule", icon: "calendar" },
  { id: "visualizer", label: "Visualizer", icon: "bolt" },
  { id: "models", label: "3D Models", icon: "layers" },
  { id: "specs", label: "Specs", icon: "ruler" },
  { id: "codes", label: "Codes", icon: "book" },
  { id: "guide", label: "Guide", icon: "help" },
  { sep: true },
  { id: "soon-permit", label: "PermitPulse", icon: "mappin", beta: true },
];

function ComingSoon({ kind, go }: any) {
  const meta = {
    "soon-permit": { icon: "mappin", title: "PermitPulse", blurb: "Auto-checks the parcel's jurisdiction against the Codes database — flags height limits, setbacks, HOA, and pool barriers, then pre-fills the permit packet for that address.", tags: ["Jurisdiction lookup", "Setback & height checks", "Permit packet"] },
  }[kind] || { icon: "bolt", title: "Coming soon", blurb: "", tags: [] };
  return (
    <div className="page fade-up" style={{ maxWidth: 760 }}>
      <div className="card pad" style={{ textAlign: "center", padding: "56px 32px", borderColor: "var(--teal-tint-2)", boxShadow: "inset 0 0 80px -40px var(--teal-glow)" }}>
        <div style={{ width: 60, height: 60, margin: "0 auto 18px", borderRadius: 16, display: "grid", placeItems: "center", background: "var(--teal-tint)", border: "1px solid var(--teal-tint-2)", color: "var(--teal)" }}>
          <Icon name={meta.icon} size={28} />
        </div>
        <span className="chip teal" style={{ marginBottom: 14 }}>On the roadmap</span>
        <h1 className="display" style={{ fontSize: 30, margin: "10px 0 12px" }}>{meta.title}</h1>
        <p className="muted" style={{ fontSize: 15, maxWidth: 520, margin: "0 auto 22px", lineHeight: 1.6 }}>{meta.blurb}</p>
        <div className="row gap-8" style={{ justifyContent: "center", flexWrap: "wrap", marginBottom: 26 }}>
          {meta.tags.map(t => <span key={t} className="chip"><Icon name="check" size={12} style={{ color: "var(--teal)" }} />{t}</span>)}
        </div>
        <div className="row gap-12" style={{ justifyContent: "center" }}>
          <button className="btn btn-primary" onClick={() => go("estimator")}>Back to Estimator</button>
          <button className="btn btn-ghost" onClick={() => go("dashboard")}>Dashboard</button>
        </div>
      </div>
    </div>
  );
}

/* ---- notification rails ----------------------------------------------------
   The cloud realtime channel already lands every remote change in the store;
   diffing snapshots turns those into MONEY MOMENTS — quote viewed right now,
   signed, payment received, new website/booking lead — kept in a device-local
   feed (localStorage, never synced) and fired as browser notifications once
   the user enables alerts from the bell. */
// the sandbox keeps its own feed — a sample lead must never buzz the real
// app's bell, and the real feed must never show inside the sandbox
const NOTIF_KEY = DEMO.active ? "fw_demo_notifications_v1" : "fw_notifications_v1";
function loadNotifs() { try { return JSON.parse(localStorage.getItem(NOTIF_KEY) || "[]"); } catch (e) { return []; } }
function pushNotifs(items) {
  if (!items || !items.length) return;
  try {
    const stamped = items.map((x, i) => ({ ...x, id: "n" + Date.now() + "-" + i, at: Date.now(), read: false }));
    localStorage.setItem(NOTIF_KEY, JSON.stringify([...stamped, ...loadNotifs()].slice(0, 50)));
  } catch (e) { /* private mode — feed is best-effort */ }
  window.dispatchEvent(new Event("fw:notify"));
  try {
    if ("Notification" in window && Notification.permission === "granted") {
      // a kind this device already gets as a push (lib/push.ts) would buzz twice
      items.filter(x => !pushCovers(x.kind)).slice(0, 3).forEach(x => { try { new Notification("FenceWorks", { body: x.text, icon: "/icon.svg", tag: (x.kind || "") + (x.quoteId || "") }); } catch (e) {} });
    }
  } catch (e) { /* notification API unavailable */ }
}
/* the Messages button: on every page, pulses while a customer message waits */
function MessagesButton({ unread, go, active }: any) {
  const n = (unread && unread.count) || 0;
  return (
    <button className={"btn btn-ghost btn-sm topbar-msgs" + (n > 0 ? " has-unread" : "") + (active ? " on" : "")} style={{ position: "relative", padding: "6px 9px" }} onClick={() => go("messages")} aria-label={`Messages${n ? ` (${n} unread)` : ""}`} title={n ? `${n} unread customer message${n > 1 ? "s" : ""}` : "Messages"}>
      <Icon name="chat" size={16} />
      {n > 0 && <span className="topbar-badge">{n > 9 ? "9+" : n}</span>}
    </button>
  );
}
/* Notification preferences live in settings.notify[kind] = { inApp, email } —
   absent means on. The in-app kinds are what the bell and the browser alert
   carry; email is sent by the service (the hub-open alert today). */
const NOTIFY_KINDS: [string, string, boolean][] = [
  ["viewed", "A customer opens their quote", true],
  ["signed", "A quote is signed", false],
  ["payment", "A payment lands", false],
  ["lead", "A new lead arrives", false],
];
function notifyOn(settings: any, kind: string, channel: "inApp" | "email") {
  const n = settings && settings.notify && settings.notify[kind];
  return !(n && n[channel] === false);
}

function NotifBell({ go, store }: any) {
  const [open, setOpen] = React.useState<any>(false);
  const [prefs, setPrefs] = React.useState<any>(false);
  const settings = (store && store.settings) || {};
  const setPref = (kind: string, channel: string, on: boolean) => {
    const notify = { ...(settings.notify || {}), [kind]: { ...((settings.notify || {})[kind] || {}), [channel]: on } };
    store && store.setSettings && store.setSettings({ notify });
  };
  const [items, setItems] = React.useState<any>(loadNotifs);
  const [perm, setPerm] = React.useState<any>(() => (typeof Notification !== "undefined" ? Notification.permission : "unsupported"));
  React.useEffect(() => {
    const h = () => setItems(loadNotifs());
    window.addEventListener("fw:notify", h); return () => window.removeEventListener("fw:notify", h);
  }, []);
  const unread = items.filter((x) => !x.read).length;
  function toggle() {
    const opening = !open;
    setOpen(opening);
    if (opening && unread) {
      const next = items.map((x) => ({ ...x, read: true }));
      setItems(next);
      try { localStorage.setItem(NOTIF_KEY, JSON.stringify(next)); } catch (e) {}
    }
  }
  const ago = (t) => { const m = Math.floor((Date.now() - t) / 60000); return m < 1 ? "now" : m < 60 ? m + "m" : m < 1440 ? Math.floor(m / 60) + "h" : Math.floor(m / 1440) + "d"; };
  return (
    <div style={{ position: "relative" }}>
      <button className={"btn btn-ghost btn-sm" + (unread > 0 ? " has-unread" : "")} style={{ position: "relative", padding: "6px 9px" }} onClick={toggle} aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`} aria-expanded={open} title="Notifications — quote views, signatures, payments & new leads">
        <Icon name="bell" size={16} />
        {unread > 0 && <span style={{ position: "absolute", top: -2, right: -2, minWidth: 15, height: 15, borderRadius: 99, background: "var(--red)", color: "#fff", fontSize: 9.5, fontWeight: 800, display: "grid", placeItems: "center", padding: "0 3px" }}>{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <>
          <div className="cm-scrim" style={{ background: "transparent" }} onClick={() => setOpen(false)} />
          <div className="corner-menu" style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", left: "auto", width: 330, maxHeight: 420, overflowY: "auto" }}>
            <div className="cm-head" style={{ justifyContent: "space-between" }}>Notifications
              <span className="row gap-8">
                {perm === "default" && <button className="btn btn-outline btn-sm" onClick={() => Notification.requestPermission().then((p) => setPerm(p))}>Enable alerts</button>}
                {perm === "denied" && <span className="faint" style={{ fontSize: 10 }}>alerts blocked in browser settings</span>}
                <button className="btn btn-ghost btn-sm" style={{ padding: "3px 8px" }} aria-expanded={!!prefs} onClick={() => setPrefs((p: any) => !p)} title="Choose what you're told about"><Icon name="gear" size={13} /></button>
              </span>
            </div>
            <button className="cm-item push-bell-row" onClick={() => { setOpen(false); go("notifications"); }}>
              <Icon name="phone" size={14} /><span style={{ flex: 1 }}>Phone notifications</span><span className="faint" style={{ fontSize: 11 }}>{pushOnHere() ? "On here" : "Set up"}</span>
            </button>
            {prefs && (
              <div className="notif-prefs">
                <div className="row" style={{ fontSize: 10.5, color: "var(--fg-3)", letterSpacing: ".06em", textTransform: "uppercase", fontWeight: 700 }}>
                  <span style={{ flex: 1 }}>Tell me when</span><span style={{ width: 52, textAlign: "center" }}>In app</span><span style={{ width: 52, textAlign: "center" }}>Email</span>
                </div>
                {NOTIFY_KINDS.map(([kind, label, hasEmail]) => (
                  <div key={kind} className="row" style={{ fontSize: 12.5, alignItems: "center" }}>
                    <span style={{ flex: 1 }}>{label}</span>
                    <span style={{ width: 52, textAlign: "center" }}><input type="checkbox" checked={notifyOn(settings, kind, "inApp")} onChange={(e) => setPref(kind, "inApp", e.target.checked)} aria-label={label + " in app"} /></span>
                    <span style={{ width: 52, textAlign: "center" }}>{hasEmail ? <input type="checkbox" checked={notifyOn(settings, kind, "email")} onChange={(e) => setPref(kind, "email", e.target.checked)} aria-label={label + " by email"} /> : <span className="faint">—</span>}</span>
                  </div>
                ))}
                <p className="faint" style={{ fontSize: 10.5, margin: "6px 0 0" }}>In-app alerts also buzz this device once browser alerts are on. Email goes to your business email.</p>
              </div>
            )}
            {items.length === 0
              ? <p className="muted" style={{ fontSize: 12.5, padding: "14px" }}>Quote views, signatures, payments and new leads land here — and buzz this device once alerts are on. Install the app (Add to Home Screen) to take them to the truck.</p>
              : items.slice(0, 30).map((x) => (
                <button key={x.id} className="cm-item" style={{ whiteSpace: "normal", textAlign: "left", alignItems: "flex-start" }} onClick={() => { setOpen(false); if (x.route) go(x.route[0], x.route[1] || null); }}>
                  <span style={{ fontSize: 12.5, lineHeight: 1.45, flex: 1 }}>{x.text}</span>
                  <span className="faint" style={{ fontSize: 10, flexShrink: 0, marginTop: 2 }}>{ago(x.at)}</span>
                </button>
              ))}
          </div>
        </>
      )}
    </div>
  );
}

/* phone bottom bar — Home · Estimates · Work Orders · More (opens the drawer).
   Not on the Estimator (its sheet and actions own the bottom edge), Crew Mode
   (its own field navigation) or the TV wall. */
const TABBAR_HIDDEN = new Set(["estimator", "crew", "tv", "hub", "build3d"]);
function MobileTabBar({ route, go, store, unread, onMore, menuOpen }: any) {
  const role = roleOf(store);
  const todoUrgent = store ? todoUrgentCount(store) : 0;
  const tabs = [
    { id: "dashboard", label: "Home", icon: "dashboard", cap: "jobs", badge: todoUrgent },
    { id: "estimates", label: "Estimates", icon: "send", cap: "estimates", also: ["quote", "estimator"] },
    { id: "workorders", label: "Work Orders", icon: "clipboard", cap: "jobs" },
  ].filter((t) => can(role, t.cap));
  const msgs = unread && unread.count > 0 ? unread.count : 0;
  return (
    <nav className="mobile-tabbar" aria-label="Quick navigation">
      {tabs.map((t) => (
        <button key={t.id} className={route === t.id || (t.also && t.also.includes(route)) ? "on" : ""} onClick={() => go(t.id)} aria-label={t.label} aria-current={route === t.id ? "page" : undefined}>
          <span className="sb-ico-wrap"><Icon name={t.icon} size={20} />{t.badge > 0 && <span className="sb-badge">{t.badge > 9 ? "9+" : t.badge}</span>}</span>{t.label}
        </button>
      ))}
      <button className={menuOpen ? "on" : ""} onClick={onMore} aria-label="More" aria-expanded={!!menuOpen}>
        <span className="sb-ico-wrap"><Icon name="menu" size={20} />{msgs > 0 && <span className="sb-badge">{msgs > 9 ? "9+" : msgs}</span>}</span>More
      </button>
    </nav>
  );
}

function App({ auth }: any) {
  const [state, api, sync] = useStore();
  // ---- the service lens (lib/verticals.ts) ----
  // fence unless this person is a platform admin (the FW Admin flag /support/me
  // reports), the workspace runs more than one service (workspaces.verticals,
  // server-owned) and this isn't the sandbox. The pick is per device
  // (localStorage) and the sidebar's switcher fires fw:vertical on change.
  const supportAdmin = useSupportAdmin();
  const [lensPref, setLensPref] = React.useState<any>(() => readLensPref());
  React.useEffect(() => {
    const h = () => setLensPref(readLensPref());
    window.addEventListener(LENS_EVENT, h); return () => window.removeEventListener(LENS_EVENT, h);
  }, []);
  const enabledLenses = enabledVerticals(state.settings, supportAdmin && supportAdmin.verticals);
  const lens = activeVertical(enabledLenses, !!(supportAdmin && supportAdmin.admin), DEMO.active, lensPref);
  api.setLens(lens);
  // every overlay (modal scrims, previews, the confirm dialog) gets focus
  // trapping, Escape-to-close and aria from ONE global hook — no per-modal code
  React.useEffect(() => { installDialogA11y(); }, []);
  // pending team invite (stashed from ?invite=… before login) → accept it now
  // that we're signed in, then reload into the joined workspace.
  React.useEffect(() => {
    let tok = null; try { tok = localStorage.getItem("fw_pending_invite"); } catch {}
    const DB: any = window.VFP_DB;
    if (!tok || !DB || !DB.teamAccept || !DB.apiBase || !DB.apiBase()) return;
    try { localStorage.removeItem("fw_pending_invite"); } catch {}
    DB.teamAccept(tok).then((r: any) => {
      if (r && r.ok) { try { localStorage.removeItem(LS_KEY); } catch {} window.location.reload(); }
    }).catch(() => {});
  }, []);
  // ---- URL router (History API — clean paths, no #) ----
  // The route lives in the pathname: app.fenceworks.ai/ = dashboard,
  // /estimates, /estimator?loadProject=p1 … so Back/Forward work and URLs
  // read like real pages (nginx serves the shell for any path). Scalar
  // params round-trip through the query string; complex params (snapshots,
  // prefill objects) ride a ref for in-app navigations and are simply
  // absent on Back — screens already guard for that. The Stripe checkout
  // return (/account?checkout=success|cancel) parses naturally.
  const parsePath = React.useCallback(() => {
    const m = (window.location.pathname || "/").match(/^\/([\w-]+)\/?$/);
    const r = m ? m[1] : "dashboard";
    // public routes are Root's; /demo is the sandbox entry. A bare /widget (no
    // token) is the contractor's Estimator Widget settings, so it deep-links
    // (lifecycle emails link there); /widget/<token> never reaches here.
    if (/^(hub|chat|build3d|demo)$/.test(r)) return { route: "dashboard", params: null };
    const p: any = {};
    new URLSearchParams(window.location.search || "").forEach((v, k) => { p[k] = v; });
    return { route: m ? r : "dashboard", params: Object.keys(p).length ? p : null };
  }, []);
  const init = React.useMemo(parsePath, []);
  const [route, setRoute] = React.useState<any>(init.route);
  const [params, setParams] = React.useState<any>(init.params);
  const [menu, setMenu] = React.useState<any>(false);
  const richParamsRef = React.useRef<any>(null);
  // in-app activity: every screen opened (lib/track.ts); clicks/errors wire once
  React.useEffect(() => { initTracking(); }, []);
  React.useEffect(() => { trackPage(route); }, [route]);
  // crash reports (lib/crash.ts) never carry a name, email or phone this account holds
  const crashStateRef = React.useRef<any>(state); crashStateRef.current = state;
  React.useEffect(() => { setCrashRedactor(() => sensitiveStrings(crashStateRef.current, auth && auth.user)); }, [auth && auth.user]);
  const go = React.useCallback((r, p = null) => {
    const flat: any = {};
    let rich: any = null;
    if (p) for (const [k, v] of Object.entries(p)) {
      if (v == null) continue;
      if (typeof v === "object") { (rich = rich || {})[k] = v; }
      else flat[k] = String(v);
    }
    if (EMBED) flat.embed = "1";   // the frame stays chrome-less across in-frame navigation
    const qs = new URLSearchParams(flat).toString();
    const target = "/" + (r === "dashboard" && !qs ? "" : r) + (qs ? "?" + qs : "");
    if (window.location.pathname + window.location.search !== target) {
      window.history.pushState({}, "", target);   // pushState doesn't fire popstate — apply below
    }
    setRoute(r); setParams(p);
    setMenu(false); window.scrollTo(0, 0);
  }, []);
  React.useEffect(() => {
    const apply = () => {
      const { route: r, params: p } = parsePath();
      const rich = richParamsRef.current;
      richParamsRef.current = null;
      setRoute(r);
      setParams(rich && rich.route === r ? Object.assign({}, p, rich.rich) : p);
      setMenu(false); window.scrollTo(0, 0);
    };
    window.addEventListener("popstate", apply);   // browser Back/Forward
    return () => window.removeEventListener("popstate", apply);
  }, []);
  // checkout return banner is one-shot: strip the query so refresh/Back don't replay it
  React.useEffect(() => {
    if (params && params.checkout) window.history.replaceState({}, "", "/account");
  }, [params && params.checkout]);
  const goBack = React.useCallback(() => {
    if (window.history.length > 1) window.history.back();
    else go("dashboard");
  }, [go]);

  // ---- unread messages (sidebar + customers badges) ----
  const [unreadCloud, setUnreadCloud] = React.useState<any>({ count: 0, convos: [], customers: [] });
  // local threads (sandbox / offline): a customer message newer than the last
  // time that thread was opened counts as unread
  const [seenTick, setSeenTick] = React.useState<any>(0);
  React.useEffect(() => { const h = () => setSeenTick((x: number) => x + 1); window.addEventListener("fw:unread", h); return () => window.removeEventListener("fw:unread", h); }, []);
  const unreadLocal = React.useMemo(() => {
    let seen: any = {}; try { seen = JSON.parse(localStorage.getItem("fw_msgs_seen_v1") || "{}"); } catch (e) {}
    const th = state.threads || {};
    const customers: string[] = [];
    Object.keys(th).forEach((cid) => {
      if (cid === "__team") return;
      const last = (th[cid] || []).filter((m: any) => m && m.from === "customer").slice(-1)[0];
      if (last && (Number(last.at) || 0) > (Number(seen[cid]) || 0)) customers.push(cid);
    });
    return { count: customers.length, customers };
  }, [state.threads, seenTick]);
  const unread = React.useMemo(() => ({ ...unreadCloud, count: (unreadCloud.count || 0) + unreadLocal.count, customers: [...(unreadCloud.customers || []), ...unreadLocal.customers] }), [unreadCloud, unreadLocal]);
  // a customer message lands → ping everywhere (bell feed + browser alert + the pulsing Messages button)
  const msgPrev = React.useRef<any>(null);
  React.useEffect(() => {
    const th = state.threads || {};
    const sig: any = {};
    Object.keys(th).forEach((cid) => { const last = (th[cid] || []).filter((m: any) => m && m.from === "customer").slice(-1)[0]; if (last) sig[cid] = Number(last.at) || 0; });
    const prev = msgPrev.current; msgPrev.current = sig;
    if (!prev) return;   // first snapshot is the baseline — a reload doesn't re-ping old threads
    const events: any[] = [];
    Object.keys(sig).forEach((cid) => {
      if (sig[cid] > (prev[cid] || 0)) {
        const c = (state.customers || []).find((x: any) => x.id === cid) || {};
        const last = (th[cid] || []).filter((m: any) => m && m.from === "customer").slice(-1)[0];
        events.push({ kind: "message", text: `💬 ${c.name || "A customer"}: “${String(last.text || "").slice(0, 110)}${String(last.text || "").length > 110 ? "…" : ""}”`, route: ["messages", { customerId: cid }] });
      }
    });
    if (events.length) pushNotifs(events);
  }, [state.threads]);
  React.useEffect(() => {
    if (!(auth && auth.user)) return;
    const DB: any = window.VFP_DB;
    if (DEMO.active || !(DB && DB.chat && DB.chat.unreadInfo)) return;   // the sandbox counts its local threads instead
    let dead = false;
    const tick = () => DB.chat.unreadInfo().then((u: any) => { if (!dead && u) setUnreadCloud(u); }).catch(() => {});
    tick();
    const t = setInterval(tick, 30000);
    window.addEventListener("fw:unread", tick);
    return () => { dead = true; clearInterval(t); window.removeEventListener("fw:unread", tick); };
  }, [auth && auth.user]);

  // The lens scopes the DATA (docs/SERVICE_SWITCHER_PLAN.md §1): every money
  // and job screen — Dashboard, To-Do, Estimates, Work Orders, Reports, Sales
  // Team, Budget & Cash, the ⌘K index, exports — sees only this service's
  // quotes, projects and work orders. A fence-only account has no tagged
  // record, so the filter keeps every one of them and those screens are what
  // they were. The company-wide screens (one customer list, one calendar, one
  // crew day, a quote opened by its id whichever service it is, the
  // estimators) get `companyStore`: the same store over the unscoped arrays.
  const companyStore = { ...state, ...api, lens, enabledLenses, canSwitchLens: canSwitch(enabledLenses, !!(supportAdmin && supportAdmin.admin), DEMO.active), allProducts: window.VFP.PRODUCTS, productsCreated: window.VFP.PRODUCTS.length,
    allQuotes: state.quotes, allProjects: state.projects, allWorkOrders: state.workOrders };
  const scoped = (arr: any[]) => (arr || []).filter((r: any) => inLens(r, lens));
  const store = { ...companyStore, quotes: scoped(state.quotes), projects: scoped(state.projects), workOrders: scoped(state.workOrders) };
  const stateRef = React.useRef<any>(state); stateRef.current = state;
  // job photos (lib/media.ts): ones that couldn't upload yet, and the old
  // base64 thumbnails, move to storage quietly — a few a minute, once the
  // cloud copy has loaded — and the diff sync saves the jobs that changed
  const syncRef = React.useRef<any>(sync); syncRef.current = sync;
  React.useEffect(() => {
    if (DEMO.active || !(auth && auth.user)) return;
    return startMediaSync({
      getState: () => stateRef.current, updateWorkOrder: api.updateWorkOrder, updateQuote: api.updateQuote,
      ready: () => ["idle", "saved", "saving", "saveerr"].includes(syncRef.current),
      notify: (m) => notify(m, { kind: "info", ms: 8000 }),
    });
  }, [auth && auth.user]);
  // the sandbox publishes its live store for the guided tour + the demo smoke test
  if (DEMO.active) { DEMO.store = store; }

  // ---- a signed pay-on-completion quote becomes a job at once (§13 item 16) ----
  // Its own deposit is 0 (a same-day lift: Settings → Payments, per service),
  // so there is no deposit to wait for; the paid path (quote.tsx recordPayment)
  // and the sandbox below create the project for everyone else. Guarded by
  // addProjectFromQuote itself, so a re-run is a no-op.
  React.useEffect(() => {
    (state.quotes || []).forEach((q: any) => {
      if (q && q.status === "signed" && q.depositPct != null && Number(q.depositPct) === 0 && (q.depositAmt == null || Number(q.depositAmt) === 0)
        && !(state.projects || []).some((p: any) => p.fromQuote === q.id)) api.addProjectFromQuote(q, null);
    });
  }, [state.quotes]);
  // ---- sandbox: a deposit "paid" on the customer hub (mocked) becomes a job ----
  // The real flow does this from the Quote screen's paid path; the hub's
  // mock checkout writes status=paid straight into the store, so convert here.
  React.useEffect(() => {
    if (!DEMO.active) return;
    (state.quotes || []).forEach((q: any) => {
      if (q && q.status === "paid" && !(state.projects || []).some((p: any) => p.fromQuote === q.id)) api.addProjectFromQuote(q, null);
    });
  }, [DEMO.active && state.quotes]);
  // hub actions land in this tab's localStorage (same device) or on the
  // server session (visitor's phone) — pick both up.
  React.useEffect(() => {
    if (!DEMO.active) return;
    const onStorage = (e: StorageEvent) => { if (e.key === LS_KEY && e.newValue) { try { api.replaceState(JSON.parse(e.newValue)); } catch (err) {} } };
    window.addEventListener("storage", onStorage);
    let dead = false;
    const poll = async () => {
      if (dead || !DEMO.apiBase || DEMO.server.enabled === false) return;
      try {
        const d = await DEMO.api("/hub-events");
        const qs = (d && d.quotes) || {};
        Object.keys(qs).forEach((qid) => {
          const ev = qs[qid] || {};
          const cur = (stateRef.current.quotes || []).find((q: any) => q.id === qid);
          if (!cur) return;
          const patch: any = {};
          if (ev.signature && !(cur.signature && cur.signature.at)) { patch.signature = ev.signature; if (cur.status !== "paid") patch.status = "signed"; }
          if (ev.paid && ev.paid.amount > 0 && cur.status !== "paid") {
            patch.status = "paid"; patch.depositPaid = (Number(cur.depositPaid) || 0) + ev.paid.amount;
            patch.stripePayments = [...(cur.stripePayments || []), { amount: ev.paid.amount, method: "stripe", source: "sandbox", kind: ev.paid.kind || "deposit", at: ev.paid.at || Date.now(), sessionId: ev.paid.sessionId || null }];
            patch.signedTotal = cur.signedTotal != null ? cur.signedTotal : cur.total;
          }
          // change orders she approved on the portal → approved here, total re-footed
          if (Array.isArray(ev.changeOrders) && ev.changeOrders.length) {
            const cos = Array.isArray(cur.changeOrders) ? cur.changeOrders : [];
            let changed = false;
            const next = cos.map((c: any) => { const a = c && ev.changeOrders.find((x: any) => x && x.id === c.id); if (a && a.approval && !(c.approval && c.approval.at)) { changed = true; return { ...c, status: "approved", approval: a.approval }; } return c; });
            if (changed) {
              const signedTotal = cur.signedTotal != null ? Number(cur.signedTotal) : Number(cur.total) || 0;
              const applied = next.filter((c: any) => c && (!c.status || c.status === "approved")).reduce((a: number, c: any) => a + (Number(c.amount) || 0), 0);
              patch.changeOrders = next; patch.total = Math.max(0, Math.round((signedTotal + applied) * 100) / 100);
              api.addActivity(`${cur.customer || "Customer"} approved a change order on the hub — new total ${window.VFP.money0(patch.total)}`);
              pushNotifs([{ kind: "signed", quoteId: qid, text: `✍️ ${cur.customer || "Customer"} approved the change order — new total ${window.VFP.money0(patch.total)}`, route: ["quote", { quoteId: qid }] }]);
            }
          }
          if (Object.keys(patch).length) { api.updateQuote(qid, patch); if (patch.status) api.addActivity(`${cur.customer || "Customer"} ${patch.status === "paid" ? "paid a deposit on the hub" : "signed the quote"}`); }
        });
      } catch (e) { /* best-effort */ }
    };
    const t = setInterval(poll, 5000);
    return () => { dead = true; clearInterval(t); window.removeEventListener("storage", onStorage); };
  }, []);

  // ---- "Keep this as my free workspace": apply the carried sandbox state once
  // the real (empty) workspace has hydrated. Never on top of existing data —
  // a workspace that already has records gets the new records unioned in.
  React.useEffect(() => {
    if (DEMO.active || !(auth && auth.user)) return;
    if (!["idle", "saved", "saving", "saveerr"].includes(sync)) return;
    let carry: any = null;
    try { carry = JSON.parse(localStorage.getItem("fw_promote_demo") || "null"); } catch (e) {}
    if (!carry) return;
    try { localStorage.removeItem("fw_promote_demo"); } catch (e) {}
    const cur: any = stateRef.current || {};
    const has = ((cur.quotes || []).length + (cur.customers || []).length + (cur.workOrders || []).length) > 0;
    if (!has) {
      api.replaceState(carry);
    } else {
      const union = (a: any[], b: any[]) => { const ids = new Set((a || []).map((x) => x && x.id)); return [...(a || []), ...(b || []).filter((x) => x && !ids.has(x.id))]; };
      api.replaceState({ ...cur, quotes: union(cur.quotes, carry.quotes), customers: union(cur.customers, carry.customers), projects: union(cur.projects, carry.projects), workOrders: union(cur.workOrders, carry.workOrders), customProducts: union(cur.customProducts, carry.customProducts) });
    }
    pushNotifs([{ kind: "info", route: null, text: "🎉 Your sandbox work is now your workspace — it's saving to the cloud." }]);
  }, [auth && auth.user, sync]);

  // ---- one-time repair: workspaces promoted before carryState re-keyed the
  // sandbox seed still hold "cust-demo-1"-style ids and "demo-seed-hub-*"
  // tokens. Those collide across workspaces in the cloud (global PKs, unique
  // hub tokens), so the customers table never synced and every hub link the
  // owner minted was dead. Re-key once after hydration; the next save carries
  // the real ids up and Sign & pay links start resolving.
  const seedRepaired = React.useRef(false);
  React.useEffect(() => {
    if (DEMO.active || seedRepaired.current || !(auth && auth.user)) return;
    if (!["idle", "saved", "saving", "saveerr"].includes(sync)) return;
    const cur: any = stateRef.current || {};
    if (!hasSeedIds(cur)) { seedRepaired.current = true; return; }
    seedRepaired.current = true;
    const { state: fixed, changed } = rekeyState(cur);
    if (!changed) return;
    api.replaceState(fixed);
    api.addActivity("Repaired sandbox record ids so customers and hub links sync to the cloud");
    pushNotifs([{ kind: "info", route: ["customers"], text: "🔧 Fixed records that were still using sandbox ids — customer hub links will work once this save finishes. Re-copy any Sign & pay link you sent before." }]);
  }, [auth && auth.user, sync]);

  /* watch quote changes for money moments → notification feed + browser alerts.
     Also does round-robin lead assignment when it's switched on (Sales Team). */
  const notifPrev = React.useRef<any>(null);
  React.useEffect(() => {
    const cur = state.quotes || [];
    const prev = notifPrev.current;
    notifPrev.current = cur;
    if (!prev || prev === cur) return;   // first snapshot is the baseline
    const money0 = window.VFP.money0;
    // depositPaid already holds the Stripe sum — adding stripePayments again
    // would double-count every online payment
    const paidOf = (q) => (Number(q.depositPaid) || 0)
      + (Array.isArray(q.payments) ? q.payments : []).reduce((a, p) => a + (Number(p.amount) || 0), 0);
    const prevBy: Map<any, any> = new Map(prev.map((q) => [q.id, q]));
    const events = [];
    const s = state.settings || {};
    let rrIdx = Number(s.leadRRIdx) || 0, rrUsed = false;
    cur.forEach((q) => {
      const p = prevBy.get(q.id);
      if (!p) {
        if (q.source === "widget" || q.source === "booking") {
          events.push({ kind: "lead", quoteId: q.id, text: `🔥 New ${q.source === "booking" ? "booking" : "website"} lead — ${q.customer || "customer"}${q.total ? " · ~" + money0(q.total) : ""}`, route: ["dashboard"] });
          // round-robin: hand the lead to the next roster rep the moment it arrives
          const roster = (s.salesReps || []).filter((r) => r.name);
          if (s.leadRoundRobin && !q.rep && roster.length) {
            const rep = roster[rrIdx % roster.length].name;
            rrIdx++; rrUsed = true;
            api.updateQuote(q.id, { rep });
            api.addActivity(`Lead ${q.customer || ""} auto-assigned to ${rep} (round robin)`);
          }
        }
        return;
      }
      if (p.status !== q.status) {
        if (q.status === "viewed") events.push({ kind: "viewed", quoteId: q.id, text: `👀 ${q.customer || "A customer"} is viewing their ${money0(q.total || 0)} quote right now — good time to call`, route: ["quote", { quoteId: q.id }] });
        else if (q.status === "signed") events.push({ kind: "signed", quoteId: q.id, text: `✍️ ${q.customer || "Customer"} signed — ${money0(q.total || 0)}`, route: ["quote", { quoteId: q.id }] });
      }
      const d = paidOf(q) - paidOf(p);
      if (d > 0.5) events.push({ kind: "payment", quoteId: q.id, text: `💵 ${money0(d)} received from ${q.customer || "customer"}`, route: ["quote", { quoteId: q.id }] });
    });
    if (rrUsed) api.setSettings({ leadRRIdx: rrIdx });
    pushNotifs(events.filter((e: any) => notifyOn(s, e.kind, "inApp")));
  }, [state.quotes]);
  const settings = state.settings;

  /* Supplier said YES: a purchase order that went out by text flips to placed
     when the yard replies. The reply lands in the SMS threads (the service
     persists inbound texts); every minute, any PO still "prepared" with a
     supplier cell is checked against that thread for a confirmation newer
     than the order. Same transition as tapping "Mark as sent". */
  React.useEffect(() => {
    const DB: any = window.VFP_DB;
    if (!(auth && auth.user) || !(DB && DB.sms && DB.sms.list && DB.apiBase && DB.apiBase())) return;
    let dead = false;
    const digits = (p: any) => String(p || "").replace(/\D/g, "").slice(-10);
    const YES = /^\s*(yes|y|yep|yup|ok|okay|confirmed|confirm|got it|will do|👍)\b/i;
    const tick = async () => {
      const pos = ((state.settings && state.settings.purchaseOrders) || []).filter((p: any) => p.status === "prepared" && digits(p.supplierPhone).length === 10);
      if (!pos.length) return;
      let threads: any[] = [];
      try { threads = (await DB.sms.list()) || []; } catch (e) { return; }
      // one settings write for the whole tick — two POs confirmed in the same
      // minute must not each rebuild the list from the stale snapshot
      let list: any[] = (state.settings.purchaseOrders || []).slice();
      let changed = false;
      for (const po of pos) {
        const th = threads.find((t: any) => digits(t.phone) === digits(po.supplierPhone));
        if (!th) continue;
        let msgs: any[] = [];
        try { msgs = (await DB.sms.messages(th.id)) || []; } catch (e) { continue; }
        const since = Number(po.preparedAt) || 0;
        const ok = msgs.find((m: any) => m.role === "customer" && YES.test(String(m.body || "")) && Date.parse(m.created_at) > since);
        if (!ok || dead) continue;
        list = list.map((p: any) => p.id === po.id ? { ...p, status: "sent", sentAt: Date.now(), sentVia: "text · confirmed by supplier" } : p);
        changed = true;
        const wo = (state.workOrders || []).find((w: any) => w.id === po.workOrderId);
        if (wo) api.updateWorkOrder(wo.id, { suppliesOrdered: true, supplier: po.supplier || wo.supplier || "" });
        api.addActivity(`${po.number} confirmed by ${po.supplier || "supplier"} by text${po.customer ? " for " + po.customer : ""}`);
        pushNotifs([{ kind: "supplies", text: `📦 ${po.supplier || "Supplier"} confirmed ${po.number}${po.customer ? " for " + po.customer : ""} — "${String(ok.body).slice(0, 60)}"`, route: ["purchaseorders", null] }]);
      }
      if (changed && !dead) api.setSettings({ purchaseOrders: list });
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => { dead = true; clearInterval(t); };
  }, [auth && auth.user, state.settings && state.settings.purchaseOrders]);

  React.useEffect(() => {
    if (window.VFP_PARCEL) window.VFP_PARCEL.setConfig({
      provider: settings.parcelProvider === "county" ? "county" : "wi",
      countyUrl: settings.countyUrl || window.VFP_PARCEL.DEFAULT_COUNTY_URL,
      statewideUrl: settings.parcelStatewideUrl || null,   // custom override — tried before the built-in source
    });
  }, [settings.parcelProvider, settings.countyUrl, settings.parcelStatewideUrl]);

  // The public estimator widget can't see the price book, so it quotes from the
  // customer $/ft ranges stamped on settings.widget.priceRanges. The widget page
  // stamps them while it's open; this keeps them current everywhere else too, so
  // a product saved or a markup changed reaches the widget on the contractor's
  // site without anyone reopening that page. Debounced; writes only on a change;
  // owner/admin tabs only (the roles that can change prices).
  React.useEffect(() => {
    const w = (settings && settings.widget) || {};
    if (!w.token || !can(roleOf(state), "money.cost")) return;
    const t = setTimeout(() => {
      let stamp = "";
      try { stamp = widgetRangeStamp(widgetPriceRanges(window.VFP.PRODUCTS || [], settings)); } catch (e) { return; }
      const cur = (stateRef.current && stateRef.current.settings && stateRef.current.settings.widget) || {};
      if (stamp !== JSON.stringify(cur.priceRanges || {})) api.setSettings({ widget: { ...cur, priceRanges: JSON.parse(stamp) } });
    }, 2000);
    return () => clearTimeout(t);
  }, [state.customProducts, settings]);

  // Signup → billing handoff. Two entry points, both handled:
  //   • from the marketing plan picker  → fw_pending_plan is set → straight to Stripe
  //   • signing up directly in the app   → fw_new_signup is set   → land on the plan picker
  // Once authenticated (real account), route a paid plan to Stripe Checkout.
  //   • from a Lead Engine preview ("Make this my website") → fw_pending_claim
  //     is set too → the site is claimed FIRST (so checkout sees the claim
  //     offer), then checkout, then the Website screen once they're back.
  React.useEffect(() => {
    if (!(auth && auth.user)) return;
    const pending = localStorage.getItem("fw_pending_plan");
    const fresh = localStorage.getItem("fw_new_signup") === "1";
    ["fw_prefill_email", "fw_prefill_name", "fw_prefill_company", "fw_prefill_heard", "fw_prefill_heard_detail", "fw_prefill_attribution"].forEach(k => localStorage.removeItem(k));
    localStorage.removeItem("fw_pending_plan");
    localStorage.removeItem("fw_new_signup");
    const DB = window.VFP_DB;
    claimPendingSite(DB).then((claimed) => {
      const welcome = () => { if (claimed) { go("website"); notify(CLAIM_READY, { kind: "success", ms: 9000 }); } };
      if (pending === "free") return welcome();            // they explicitly chose Free
      if (pending === "managed") {                         // paid on fenceworks.ai/managed through Stripe's link; we set them up (docs/MANAGED_PLAN.md)
        notify("Welcome aboard. Your Managed Office is being set up — we'll call to book your setup call within one business day.", { kind: "success", ms: 12000 });
        return welcome();
      }
      if (!pending && !fresh) return welcome();            // returning user, nothing to do
      if (!(DB && DB.billingCheckout && DB.apiBase && DB.apiBase())) { if (fresh && !pending) go("account"); return; }
      DB.billingStatus().then(r => {
        const sub = r && r.subscription;
        // Only a PAID active/trialing plan counts as subscribed — the operator
        // workspace is seeded free/active, which must NOT block a real checkout.
        const subscribed = sub && sub.plan && sub.plan !== "free" && PAID_STATUS.has(sub.status);
        if (subscribed) return welcome();
        if (!pending) { go("account"); return; }  // in-app signup → let them pick a plan
        DB.billingCheckout(pending)
          .then(res => {
            if (res && res.url) {
              // back from Stripe → the Website screen (the effect below)
              if (claimed) { try { localStorage.setItem("fw_claim_welcome", "1"); } catch (e) {} }
              window.location.href = res.url;
            } else go("account");
          })
          .catch(e => { console.error("[billing] checkout failed:", e); go("account"); });
      }).catch(e => { console.error("[billing] status failed:", e); go("account"); });
    });
  }, [auth && auth.user]);

  // Back from checkout after claiming a Lead Engine site: open it, once.
  React.useEffect(() => {
    if (!(auth && auth.user)) return;
    let w = null; try { w = localStorage.getItem("fw_claim_welcome"); } catch (e) {}
    if (!w) return;
    try { localStorage.removeItem("fw_claim_welcome"); } catch (e) {}
    go("website");
    notify(CLAIM_READY, { kind: "success", ms: 9000 });
  }, [auth && auth.user]);

  // Different account signed in on this browser → drop the previous user's
  // locally-cached store so their data can't show through before the cloud
  // loads (this is what made a fresh signup look like it "saw the demo data").
  React.useEffect(() => {
    const uid = auth && auth.user && auth.user.id;
    if (!uid) return;
    let last = null; try { last = localStorage.getItem("fw_last_uid"); } catch (e) {}
    if (last && last !== uid) { try { localStorage.removeItem(LS_KEY); } catch (e) {} api.reset(); }
    try { localStorage.setItem("fw_last_uid", uid); } catch (e) {}
  }, [auth && auth.user && auth.user.id]);

  // Keep settings.plan (and subStatus) synced with the REAL (Stripe-backed)
  // subscription so gating and the sidebar chip reflect truth everywhere — not
  // just after a visit to the Account page. A paid sub keeps its plan while
  // Stripe is still collecting (active / trialing / past_due, the last with an
  // "update your card" banner); anything else (canceled, unpaid, no sub) is Free.
  // Re-checked on load, every five minutes, when the tab regains focus, and
  // whenever a service call answers 403 {error:"plan"} (window.VFP_RESYNC_PLAN).
  const planSyncing = React.useRef(false);
  const settingsRef = React.useRef<any>(state.settings); settingsRef.current = state.settings;   // latest settings for the timer / focus callbacks
  const syncPlan = React.useCallback(() => {
    const DB = window.VFP_DB;
    if (planSyncing.current || !(auth && auth.user) || !(DB && DB.billingStatus && DB.apiBase && DB.apiBase())) return;
    planSyncing.current = true;
    DB.billingStatus().then(r => {
      // the printed quote's "Powered by Fence Works" is the service's call
      // (server/src/poweredby.js), never settings.plan — no answer, no badge
      window.VFP_POWERED_BY = !!(r && r.poweredBy === true);
      if (!r) return;   // no answer → don't touch the plan (a failure must never downgrade to Free)
      const sub = r && r.subscription;
      const paid = sub && sub.plan && sub.plan !== "free" && PAID_STATUS.has(sub.status);
      const plan = paid ? sub.plan : "free";
      const subStatus = (sub && sub.status) || null;
      const cur = (settingsRef.current || {}) as any;
      if ((cur.plan || "free") !== plan || (cur.subStatus || null) !== subStatus) api.setSettings({ plan, subStatus });   // unchanged → no write
    }).catch(() => { window.VFP_POWERED_BY = false; }).finally(() => { planSyncing.current = false; });
  }, [auth && auth.user]);
  /* The business's timezone. The server judges texting quiet hours in
     settings.timezone, and nothing ever wrote it — so every contractor was
     held to the server's zone, and "8am" in Chicago was 6am in California.
     The first signed-in, hydrated browser records its own zone; a contractor
     can still change it later. Only fills a gap, never overwrites. */
  React.useEffect(() => {
    // only once the cloud state has loaded (idle / saving / saved) — never
    // onto a blank profile that hasn't hydrated yet
    if (DEMO.active || !(auth && auth.user) || !["idle", "saving", "saved"].includes(sync)) return;
    if (state.settings && (state.settings as any).timezone) return;
    let tz = "";
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch (e) {}
    if (tz) api.setSettings({ timezone: tz });
  }, [auth && auth.user, sync, state.settings && (state.settings as any).timezone]);
  // My role in THIS workspace, straight from the team roster the service owns.
  // Unresolved (solo, offline, sandbox) stays "owner" so nobody is ever locked
  // out of their own account by a failed lookup.
  React.useEffect(() => {
    if (!(auth && auth.user) || DEMO.active) return;
    const DB: any = window.VFP_DB;
    if (!(DB && DB.teamMembers && DB.apiBase && DB.apiBase())) return;
    let dead = false;
    DB.teamMembers().then((r: any) => {
      if (dead || !r) return;
      const me = (r.members || []).find((m: any) => m.id === r.you);
      const role = normalizeRole(me && me.role);
      if (((settingsRef.current || {}) as any).role !== role) api.setSettings({ role });
    }).catch(() => {});
    return () => { dead = true; };
  }, [auth && auth.user]);

  React.useEffect(() => {
    if (sync === "loading" || !(auth && auth.user)) return;
    window.VFP_RESYNC_PLAN = syncPlan;
    syncPlan();
    const every = setInterval(syncPlan, 5 * 60 * 1000);
    const onVisible = () => { if (document.visibilityState === "visible") syncPlan(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(every); document.removeEventListener("visibilitychange", onVisible); if (window.VFP_RESYNC_PLAN === syncPlan) window.VFP_RESYNC_PLAN = null; };
  }, [sync, auth && auth.user, syncPlan]);

  // Trial-status + usage bootstrap → feeds window.VFP_CREDITS (separate PR) so its
  // creditState/isTrial reflect the REAL Stripe trial & metered usage app-wide,
  // not just on the Account page. Best-effort: feature-detected, errors swallowed,
  // no-op without a live service. Re-runs on a checkout return (params.checkout).
  React.useEffect(() => {
    if (!(auth && auth.user)) return;
    const DB: any = window.VFP_DB;
    if (!(DB && DB.apiBase && DB.apiBase())) return;
    try {
      if (params && params.checkout && window.VFP_RESYNC_PLAN) window.VFP_RESYNC_PLAN();   // back from Stripe → read the new plan now
      if (DB.billingUsage) DB.billingUsage()
        .then((r: any) => { if (r) api.setSettings({ usageCache: r }); })
        .catch(() => {});
    } catch (e) { /* never let the bootstrap break mount */ }
  }, [auth && auth.user, params && params.checkout]);

  const screens = {
    dashboard: <Dashboard store={store} go={go} auth={auth} />,
    // under the Concrete lens "New estimate" is the typed concrete estimate (phase 1); the fence estimator is untouched
    // the estimator follows the lens — or the quote being edited, whichever service it is
    estimator: (lens === "concrete" || (params && params.quoteId && (state.quotes || []).some((x: any) => x && x.id === params.quoteId && x.vertical === "concrete")))
      ? <ConcreteEstimatePage key={(params && params.quoteId) || "new"} store={companyStore} go={go} params={params} />
      : <Estimator key={params && params.loadProject || "new"} store={companyStore} go={go} params={params} settings={settings} auth={auth} />,
    estimates: <EstimatesPage store={store} go={go} />,
    leadradar: <LeadRadarPage store={store} go={go} />,
    dataio: <DataIOPage store={store} go={go} params={params} sync={sync} />,
    reviews: <ReviewsPage store={store} go={go} params={params} />,
    gbp: <GbpOptimizerPage store={store} go={go} />,
    website: <WebsitePage store={store} go={go} params={params} auth={auth} />,
    reports: <ReportsPage store={store} go={go} />,
    business: <BusinessPage store={store} go={go} />,
    salesteam: <SalesTeamPage store={store} go={go} auth={auth} />,
    tv: <TVDisplay store={store} />,
    crew: <CrewMode store={companyStore} go={go} params={params} />,   // the crew's day is every service's jobs
    inventory: <InventoryPage store={store} go={go} />,
    purchaseorders: <PurchaseOrdersPage store={store} go={go} />,
    messages: <MessagesPage store={companyStore} go={go} params={params} />,
    permits: <PermitCheck store={store} go={go} />,
    // a quote opens by its id whichever service it is — the record, not the lens
    quote: <><ConcreteQuoteNote store={companyStore} params={params} /><Quote store={companyStore} go={go} goBack={goBack} params={params} settings={settings} /></>,
    products: <Products store={store} go={go} />,
    // key by category too, so "new Vinyl product" remounts on a fresh template
    "product-edit": <ProductEditor key={(params && params.id) || ("new:" + ((params && params.category) || "") + ":" + ((params && params.duplicate) || ""))} store={store} go={go} params={params} />,
    // a concrete rate card (settings.rateCards[jobType]; concrete.tsx) — one per job type
    ratecard: <RateCardEditor key={(params && params.jobType) || "none"} store={store} go={go} params={params} />,
    sequences: <EmailSequences store={store} go={go} params={params} />,
    settings: <Settings store={store} go={go} params={params} sync={sync} />,
    integrations: <Integrations store={store} go={go} />,
    financing: <FinancingPage store={store} go={go} />,
    account: <AccountBilling store={store} go={go} auth={auth} params={params} />,
    notifications: <NotificationsPage store={store} go={go} />,   // phone + desktop notifications, every role (pushsettings.tsx)
    hub: <CustomerHubSettings store={store} go={go} />,
    jobpacket: <JobPacketPage store={store} go={go} />,
    swag: <JobPacketPage store={store} go={go} section="swag" />,   // FW Admin: the partner order builder
    printkit: <PrintKitPage store={store} go={go} auth={auth} params={params} />,
    printadmin: <PrintOrdersAdmin params={params} go={go} />,
    widget: <EstimatorWidgetSettings store={store} go={go} params={params} />,
    specs: <SpecsPage store={store} />,
    codes: <CodesPage />,
    visualizer: <Visualizer store={store} go={go} params={params} />,
    models: <ModelsPage go={go} />,
    ai: <AISuite store={store} go={go} params={params} />,
    phone: <PhonePage store={store} go={go} auth={auth} />,   // the business line: who rings, voicemail, texts (docs/VOIP_PLAN.md)
    automation: <AutomationPage store={store} go={go} params={params} />,
    workorders: <WorkOrders store={store} go={go} params={params} auth={auth} />,
    calendar: <CalendarView store={companyStore} go={go} params={params} />,   // one calendar for the company: every service's jobs, chipped
    customers: <Customers store={companyStore} go={go} params={params} />,   // one customer list; a profile shows every service's jobs, chipped
    todo: <TodoPage store={store} go={go} />,
    guide: <Guide go={go} />,
    support: <SupportInbox auth={auth} params={params} go={go} />,
    // FW Admin (platform owner only — the service 403s everyone else)
    ops: <OpsPage />,
    deletions: <DeletionRequestsPage go={go} />,
    fwadmins: <PlatformAdminsPage go={go} />,   // FW Admin → Admins (platform admins; migration #33)
    fwlines: <PhoneLinesAdminPage go={go} />,   // FW Admin → Phone lines (every tenant's business lines; migration #48)
    fwstatus: <PlatformStatusPage go={go} />,   // FW Admin → Status & To-do (platform admins; migration #35)
    fwcustomers: <CustomersAdminPage params={params} go={go} />,
    lifecycle: <LifecycleEmailsPage go={go} />,
    growth: <GrowthPage go={go} />,
    // FW Admin → Lead Engine (leadengine.tsx; the service's /leadengine routes are owner-gated)
    leadengine: <LeadEngineHome params={params} go={go} />,   // ?prospect=<id> (the hot alert's link) opens that prospect
    "leadengine-find": <LeadFindPage go={go} />,
    "leadengine-today": <LeadTodayPage go={go} />,
    "leadengine-outreach": <OutreachPage go={go} params={params} />,   // outreach: queue, sequence, sent, do-not-contact, setup
    prospect: <ProspectPage key={(params && params.id) || "none"} params={params} go={go} />,
  };

  if (EMBED) {
    const full = "/demo?go=" + encodeURIComponent(route) + (params ? Object.entries(params).filter(([k, v]) => v != null && typeof v !== "object" && k !== "embed").map(([k, v]) => "&" + encodeURIComponent(k) + "=" + encodeURIComponent(String(v))).join("") : "") + "&utm_source=fenceworks.ai&utm_medium=embed&utm_campaign=demo&utm_content=" + encodeURIComponent(route);
    return (
      <div className="app-shell embed-shell">
        <div className="embed-bar"><span><b>Sandbox preview</b> · live, nothing saved</span><a href={full} target="_top" rel="noopener">Open the full demo ↗</a></div>
        <div className="app-main"><div className="app-main-scroll">
          <PageErrorBoundary routeKey={route} go={go}>{screens[route] || <NotFound go={go} />}</PageErrorBoundary>
        </div></div>
        <Toaster />
        <ConfirmHost />
      </div>
    );
  }
  return (
    <div className={"app-shell" + (DEMO.active ? " demo-shell" : "") + (TABBAR_HIDDEN.has(route) ? "" : " has-tabbar")}>
      {DEMO.active && <DemoBanner store={store} go={go} />}
      <Sidebar route={route} section={params && params.section} go={go} auth={auth} store={store} unread={unread} mobileOpen={menu} setMobileOpen={setMenu} />
      {menu && <div className="sb-backdrop" onClick={() => setMenu(false)} />}
      <div className="app-main">
        {sync === "error" && <CloudUnreachableBanner />}
        {!DEMO.active && store.settings.subStatus === "past_due" && (store.settings.plan || "free") !== "free" && <PastDueBanner plan={store.settings.plan} go={go} />}
        {!DEMO.active && <WhatsNew go={go} />}
        <div className="main-topbar">
          <button className="sb-mobile" onClick={() => setMenu(m => !m)} aria-label="Menu" aria-expanded={!!menu}><Icon name="menu" size={20} /></button>
          <span className="mt-logo" style={{ display: "none", alignItems: "center" }} onClick={() => go("dashboard")}><LogoMark size={24} /></span>
          <span className="crumb">{CRUMB[route] || "Fence Works"}{lens !== "fence" ? " · " + VERTICALS[lens].label : ""}</span>
          <div className="topbar-search" style={{ flex: 1, minWidth: 0, display: "flex", justifyContent: "center", padding: "0 12px" }}><GlobalSearch store={store} go={go} /></div>
          <MessagesButton unread={unread} go={go} active={route === "messages"} />
          <PageGuideButton route={route} go={go} />
          <NotifBell go={go} store={store} />
          <SyncDot sync={sync} />
        </div>
        <div className="app-main-scroll">
          <PageErrorBoundary routeKey={route + (params && params.section ? ":" + params.section : "")} go={go}>
            {(() => {
              // role first: a screen this teammate can't use isn't an upsell
              const myRole = roleOf(store);
              const needCap = ROUTE_CAP[route];
              if (needCap && !can(myRole, needCap))
                return <RoleGate label={CRUMB[route] || "That screen"} role={myRole} go={go} />;
              // a fence-only screen under the Concrete lens (lib/verticals.ts hiddenRoutes): the way back, never a blank
              if (routeHidden(route, lens))
                return <LensGate label={CRUMB[route] || "That screen"} lens={lens} go={go} />;
              const need = ROUTE_GATE[route];
              if (need && window.VFP_PLAN && !window.VFP_PLAN.allows(store, need.cap))
                return <UpgradeGate feature={need.label} need={need.plan} go={go} />;
              return screens[route] || <NotFound go={go} />;
            })()}
          </PageErrorBoundary>
        </div>
      </div>
      {!TABBAR_HIDDEN.has(route) && <MobileTabBar route={route} go={go} store={store} unread={unread} menuOpen={menu} onMore={() => setMenu((m: any) => !m)} />}
      {/* the "?" help chat — every screen, straight to the founder's inbox (not in the sandbox) */}
      {route !== "tv" && !DEMO.active && <HelpBubble auth={auth} route={route} go={go} />}
      {DEMO.active && <DemoExpired />}
      {DEMO.active && route !== "tv" && <DemoTour store={store} route={route} params={params} go={go} />}
      <Toaster />
      <ConfirmHost />
      {route !== "tv" && <InstallNudge />}

    </div>
  );
}

// The boot read of the cloud failed and this tab is un-hydrated: every push is
// blocked (see useStore) so the operator needs to KNOW they're read-only, not
// infer it from a red dot. Dismissible; "Retry now" kicks the hydrate loop.
// statuses under which a subscription keeps its plan (mirrors server/src/db.js PAID_STATUSES)
const PAID_STATUS = new Set(["active", "trialing", "past_due"]);

/* A failed card inside Stripe's retry window: the plan stays on, the owner is
   told plainly, and one tap opens the Stripe portal to fix the card. When the
   retries run out the status flips and the plan drops to Free on the next sync. */
function PastDueBanner({ plan, go }: any) {
  const [busy, setBusy] = React.useState<any>(false);
  const name = ({ starter: "Starter", pro: "Pro", growth: "Growth" } as any)[plan] || "your plan";
  async function fix() {
    const DB: any = window.VFP_DB;
    setBusy(true);
    try { if (DB && DB.billingPortal) { const r = await DB.billingPortal(); if (r && r.url) { window.location.href = r.url; return; } } } catch (e) {}
    setBusy(false); go("account", { tab: "plan" });
  }
  return (
    <div role="alert" className="row gap-8" style={{ padding: "8px 14px", background: "var(--amber-tint, rgba(245,158,11,.14))", color: "var(--amber, #f59e0b)", fontSize: 13, flexWrap: "wrap", alignItems: "center", borderBottom: "1px solid var(--line)" }}>
      <Icon name="dollar" size={14} />
      <span style={{ flex: 1, minWidth: 220 }}><b>Your last payment didn't go through.</b> {name} stays on while Stripe retries the card; if the retries fail, the account drops to Free (your data stays).</span>
      <button className="btn btn-primary btn-sm" disabled={busy} onClick={fix}>{busy ? "Opening…" : "Update payment"}</button>
    </div>
  );
}

/* "What's new" — the newest changelog entry, once, on the first sign-in after
   it ships. People who see the product moving stay; a line they can dismiss
   costs nothing. */
const WHATSNEW_KEY = "fw_whatsnew_seen";
function WhatsNew({ go }: any) {
  const latest = CHANGELOG[0];
  const [seen, setSeen] = React.useState<any>(() => { try { return localStorage.getItem(WHATSNEW_KEY) || ""; } catch (e) { return ""; } });
  if (!latest || seen === latest.id) return null;
  const dismiss = () => { try { localStorage.setItem(WHATSNEW_KEY, latest.id); } catch (e) {} setSeen(latest.id); };
  return (
    <div role="status" className="row gap-8 whatsnew" style={{ padding: "8px 14px", fontSize: 13, flexWrap: "wrap", alignItems: "center", borderBottom: "1px solid var(--line)", background: "var(--teal-tint)" }}>
      <Icon name="bolt" size={14} style={{ color: "var(--teal)" }} />
      <span style={{ flex: 1, minWidth: 220 }}><b>New: {latest.title}.</b> {latest.detail}</span>
      {latest.route && <button className="btn btn-outline btn-sm" onClick={() => { dismiss(); go(latest.route); }}>Take a look</button>}
      <button className="btn btn-ghost btn-sm" aria-label="Dismiss" onClick={dismiss}><Icon name="x" size={13} /></button>
    </div>
  );
}

function CloudUnreachableBanner() {
  const [hidden, setHidden] = React.useState<any>(false);
  if (hidden) return null;
  return (
    <div role="alert" className="row gap-8" style={{ padding: "8px 14px", background: "var(--red-tint)", color: "var(--red)", fontSize: 13, flexWrap: "wrap", borderBottom: "1px solid var(--line)" }}>
      <Icon name="x" size={14} />
      <span style={{ flex: 1, minWidth: 200 }}>Cloud unreachable — working read-only; changes stay on this device until it reconnects.</span>
      <button className="btn btn-outline btn-sm" onClick={() => { try { window.dispatchEvent(new Event("fw:retry-sync")); } catch (e) {} }}>Retry now</button>
      <button className="btn btn-ghost btn-sm" aria-label="Dismiss" onClick={() => setHidden(true)}><Icon name="x" size={13} /></button>
    </div>
  );
}

// Branded in-app 404 — shown when the URL maps to no known screen (the app's
// nginx serves index.html for every path, so unknown routes land in React).
function NotFound({ go }: any) {
  return (
    <div className="page fade-up" style={{ maxWidth: 560 }}>
      <div className="card pad" style={{ textAlign: "center", padding: "52px 28px" }}>
        <div style={{ margin: "0 auto 16px", display: "grid", placeItems: "center" }}><LogoMark size={48} /></div>
        <div className="mono" style={{ color: "var(--teal)", fontSize: 12, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 8 }}>Error 404</div>
        <h1 className="display" style={{ fontSize: 25, margin: "0 0 8px" }}>Page not found</h1>
        <p className="muted" style={{ fontSize: 14.5, maxWidth: 420, margin: "0 auto 18px" }}>That page doesn't exist or has moved. Let's get you back on track.</p>
        <div className="row gap-8" style={{ justifyContent: "center", flexWrap: "wrap" }}>
          <button className="btn btn-primary" onClick={() => go("dashboard")}>Go to dashboard</button>
          <button className="btn btn-outline" onClick={() => go("estimator")}><Icon name="map" size={14} />New estimate</button>
        </div>
      </div>
    </div>
  );
}

// Catches a render crash in any routed page so one bad page shows a recoverable
// error (with the actual message) instead of a blank screen / dead app. Resets
// itself when the route changes so navigating away clears the error.
class PageErrorBoundary extends React.Component<any, any> {
  constructor(props: any) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err: any) { return { err }; }
  componentDidCatch(err: any, info: any) {
    try { console.error("[page-crash]", err, info); } catch (e) {}
    reportError(err, { kind: "boundary", componentStack: info && info.componentStack });   // → Ops & Health → App errors
  }
  componentDidUpdate(prev: any) { if (prev.routeKey !== this.props.routeKey && this.state.err) this.setState({ err: null }); }
  render() {
    if (!this.state.err) return this.props.children;
    const msg = String((this.state.err && (this.state.err.message || this.state.err)) || "Unknown error");
    return (
      <div className="page fade-up" style={{ maxWidth: 640 }}>
        <div className="card pad" style={{ textAlign: "center", padding: "48px 28px", borderColor: "var(--red-tint)" }}>
          <div style={{ width: 52, height: 52, margin: "0 auto 14px", borderRadius: 13, display: "grid", placeItems: "center", background: "var(--red-tint)", color: "var(--red)" }}><Icon name="x" size={24} /></div>
          <h1 className="display" style={{ fontSize: 23, margin: "6px 0 8px" }}>This page hit a snag</h1>
          <p className="muted" style={{ fontSize: 14.5, maxWidth: 460, margin: "0 auto 16px" }}>Something failed to render here — your data is safe. Try reloading, or head back to the dashboard.</p>
          <pre style={{ textAlign: "left", fontSize: 12, color: "var(--fg-3)", background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 8, padding: "10px 12px", overflow: "auto", maxHeight: 160, margin: "0 0 16px", whiteSpace: "pre-wrap" }}>{msg}</pre>
          <div className="row gap-8" style={{ justifyContent: "center" }}>
            <button className="btn btn-ghost" onClick={() => this.props.go && this.props.go("dashboard")}>Go to dashboard</button>
            <button className="btn btn-primary" onClick={() => location.reload()}>Reload</button>
          </div>
        </div>
      </div>
    );
  }
}

/* which capability each screen needs (web/src/lib/roles.ts holds the map).
   Anything not listed is open to every role. */
const ROUTE_CAP: Record<string, string> = {
  dashboard: "jobs", todo: "jobs", calendar: "jobs", workorders: "jobs",
  customers: "customers", dataio: "customers", leadradar: "marketing", reviews: "marketing", gbp: "marketing", website: "marketing",
  estimator: "estimates", estimates: "estimates", quote: "estimates",
  products: "pricebook", producteditor: "pricebook",
  inventory: "purchasing", purchaseorders: "purchasing",
  reports: "reports", salesteam: "reports", business: "reports", tv: "reports",
  widget: "marketing", hub: "marketing", ai: "marketing", sequences: "marketing", printkit: "marketing", financing: "marketing",
  settings: "settings", integrations: "settings",
  account: "team",   // the screen also holds Team; the billing tabs gate themselves
};
function RoleGate({ label, role, go }: any) {
  return (
    <div className="page fade-up" style={{ maxWidth: 560 }}>
      <div className="card pad" style={{ textAlign: "center", padding: "48px 28px" }}>
        <div style={{ width: 52, height: 52, margin: "0 auto 14px", borderRadius: 14, display: "grid", placeItems: "center", background: "var(--surface-2)", border: "1px solid var(--line-2)", color: "var(--fg-3)" }}><Icon name="lock" size={24} /></div>
        <h1 className="display" style={{ fontSize: 22, margin: "6px 0 8px" }}>{label} isn't part of your access</h1>
        <p className="muted" style={{ fontSize: 14.5, maxWidth: 400, margin: "0 auto 20px" }}>
          You're signed in as <b style={{ color: "var(--fg-2)" }}>{roleLabel(role)}</b>. Ask the owner or an admin if you need this.
        </p>
        <button className="btn btn-outline" onClick={() => go(homeRouteFor(role))}><Icon name="chevright" size={15} style={{ transform: "rotate(180deg)" }} />Back to my work</button>
      </div>
    </div>
  );
}

const ROUTE_GATE = {
  workorders: { cap: "workorders", label: "Work Orders", plan: "Pro" },
  calendar: { cap: "workorders", label: "Schedule", plan: "Pro" },
  integrations: { cap: "integrations", label: "Integrations", plan: "Pro" },
  hub: { cap: "workorders", label: "Customer Hub", plan: "Pro" },
  widget: { cap: "widget", label: "Estimator Widget", plan: "Starter" },
  visualizer: { cap: "renders", label: "Visualizer", plan: "Starter" },
  ai: { cap: "integrations", label: "AI Front Desk", plan: "Pro" },
  phone: { cap: "integrations", label: "Business line", plan: "Pro" },
  inventory: { cap: "inventory", label: "Inventory", plan: "Growth" },
  purchaseorders: { cap: "inventory", label: "Purchase Orders", plan: "Growth" },
  website: { cap: "website", label: "Website", plan: "Growth" },
};
function UpgradeGate({ feature, need, go }: any) {
  return (
    <div className="page fade-up" style={{ maxWidth: 640 }}>
      <div className="card pad" style={{ textAlign: "center", padding: "56px 32px", borderColor: "var(--teal-tint-2)", boxShadow: "inset 0 0 80px -40px var(--teal-glow)" }}>
        <div style={{ width: 56, height: 56, margin: "0 auto 16px", borderRadius: 14, display: "grid", placeItems: "center", background: "var(--teal-tint)", border: "1px solid var(--teal-tint-2)", color: "var(--teal)" }}><Icon name="lock" size={26} /></div>
        <span className="chip teal" style={{ marginBottom: 12 }}>{need} plan</span>
        <h1 className="display" style={{ fontSize: 26, margin: "8px 0 10px" }}>{feature} is a {need} feature</h1>
        <p className="muted" style={{ fontSize: 15, maxWidth: 440, margin: "0 auto 22px" }}>Upgrade to unlock {feature} — plus everything below it. Founder pricing is 50% off forever if you start before {FOUNDER_ENDS_LABEL}.</p>
        <button className="btn btn-primary" onClick={() => go("account")}><Icon name="bolt" size={16} />Upgrade to {need}</button>
      </div>
    </div>
  );
}

const CRUMB = {  dashboard: "Dashboard", estimator: "Estimator", estimates: "Estimates", dataio: "Import & export", leadradar: "Lead Radar", reviews: "Reviews", gbp: "Google Profile", website: "Website", reports: "Reports", business: "Budget & Cash", salesteam: "Sales Team", tv: "TV Display", crew: "Crew Mode", inventory: "Inventory", purchaseorders: "Purchase Orders", workorders: "Work Orders", customers: "Customers", todo: "To-Do",
  products: "Products", settings: "Settings", sequences: "Email Sequences", integrations: "Integrations",
  hub: "Customer Hub", jobpacket: "Job Packet", swag: "Swag & Print Partners", printkit: "Print Kit", printadmin: "Print Orders", fwcustomers: "Customers (FW Admin)", lifecycle: "Lifecycle Emails", growth: "Growth", widget: "Estimator Widget", calendar: "Schedule", visualizer: "Visualizer", models: "3D Models",
  specs: "Specs", codes: "Codes", guide: "Guide", support: "Support Inbox", ops: "Ops & Health", deletions: "Deletion Requests", quote: "Quote", "product-edit": "Edit Product", ratecard: "Rate card", permits: "Permit Check", financing: "Financing", messages: "Messages", account: "Account & Billing", notifications: "Notifications", ai: "AI Front Desk", automation: "Automation", phone: "Business line", fwlines: "Phone lines (FW Admin)",
  leadengine: "Lead Engine", "leadengine-find": "Find prospects", "leadengine-today": "Today · Lead Engine", "leadengine-outreach": "Outreach · Lead Engine", prospect: "Prospect",
  fwadmins: "Admins (FW Admin)",
  fwstatus: "Status & To-do (FW Admin)",
};


function SyncDot({ sync }: any) {
  // "error" ≠ offline: it means the cloud READ/WRITE failed while the browser
  // may be perfectly online (Supabase incident, expired session, blocked
  // request). Say which one it is, carry the actual failure reason in the
  // tooltip, and let a click retry the boot read immediately.
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  const map = {
    loading: { c: "var(--amber)", t: "Connecting…", pulse: true },
    saving:  { c: "var(--amber)", t: "Saving…", pulse: true },
    saved:   { c: "var(--green)", t: "Saved to cloud" },
    idle:    { c: "var(--green)", t: "Cloud synced" },
    error:   offline
      ? { c: "var(--red)", t: "Offline — working locally, reconnects automatically", pulse: true }
      : { c: "var(--red)", t: "Cloud unreachable — read-only until it answers (click to retry)", pulse: true },
    nosession: { c: "var(--red)", t: "Signed out — sign in again to sync changes" },
    saveerr: { c: "var(--red)", t: "Some data didn't save — hover for what; retries on your next edit", pulse: true },
    stale:   { c: "var(--red)",   t: "Tab was behind — latest cloud data merged in; records created here were kept", pulse: true },
    guard:   { c: "var(--red)",   t: "Blocked an empty save that would have erased this workspace — latest data reloaded", pulse: true },
    local:   { c: "var(--fg-faint)", t: "Local only" },
    demo:    { c: "var(--teal)", t: "Sandbox — nothing here is saved" },
  };
  const m = map[sync] || map.idle;
  const detail = (sync === "error" || sync === "saveerr") ? String((window as any).VFP_SYNC_DETAIL || "") : "";
  return (
    <span className="sync-dot" title={m.t + (detail ? " — " + detail.slice(0, 160) : "")}
      style={sync === "error" ? { cursor: "pointer" } : undefined}
      onClick={() => { if (sync === "error") { try { window.dispatchEvent(new Event("fw:retry-sync")); } catch (e) {} } }}>
      <span className={"sd" + (m.pulse ? " pulse" : "")} style={{ background: m.c }} />
      <span className="sd-label">{m.t}</span>
    </span>
  );
}

// Fence Construction 3D is the only module that imports three — keep it out of
// the main bundle by ONLY ever loading it lazily (never via registry).
const PublicBuild3DLazy = React.lazy(() => import("./fencebuild").then(m => ({ default: m.PublicBuild3D })));

function Root() {
  const auth = useAuth();
  const [demoOff, setDemoOff] = React.useState<any>(() => DEMO.active && DEMO.server && DEMO.server.enabled === false && !!DEMO.apiBase);
  React.useEffect(() => {
    if (!DEMO.active) return;
    const h = (e: any) => setDemoOff(!!(e.detail && e.detail.enabled === false && DEMO.apiBase));
    window.addEventListener("fw:demo-server", h); return () => window.removeEventListener("fw:demo-server", h);
  }, []);
  // public share links live on clean paths now (/hub/<token>, /widget/<token>,
  // /build3d/<token>); the boot shim converts legacy #/hub/... URLs before
  // render. PublicRoute still consumes the "#/kind/token?query" shape, so
  // synthesize it.
  const pub = window.location.pathname.match(/^\/(hub|widget|chat|build3d)\/(.+)$/);
  // /build3d — public token-less 3D construction viewer (marketing embed +
  // shareable). No data fetch, no auth — purely client-side. With a token
  // (/build3d/<token>) it's the customer share page and routes via `pub`.
  const build3d = /^\/build3d\/?$/.test(window.location.pathname || "");
  const hash = pub ? `#/${pub[1]}/${pub[2]}${window.location.search || ""}` : "";
  const [pubState, setPubState] = React.useState<any>(null);
  const isPublic = !!pub;
  // load data for the public token-secured routes. Per-workspace RLS closed
  // anonymous Supabase reads, so share links resolve through the automation
  // service (token → workspace, service role). Local state stays the fallback
  // so the in-app previews and offline/demo use keep working.
  React.useEffect(() => {
    if (!pub) return;
    let local = null; try { local = JSON.parse(localStorage.getItem(LS_KEY)); } catch {}
    const fallback = local || JSON.parse(JSON.stringify(BLANK));
    const base = window.VFP_API_BASE;
    // resolve from the PATHNAME (`pub`), not the hash — clean URLs (#32) left
    // the hash empty, so the old hash match silently skipped the server fetch
    // and fell back to local state (no hubThread → hub showed no messages).
    if (pub && base) {
      // the chat bubble has no state of its own — it borrows the widget's
      // state for company branding (logo, name, accent). The /build3d/<token>
      // share page renders from the SAME one-customer state as their hub.
      const fetchKind = pub[1] === "chat" ? "widget" : pub[1] === "build3d" ? "hub" : pub[1];
      // forward the query string (?utm_…) so the service can log WHICH QR /
      // link brought this visit — the SPA otherwise swallows it client-side
      fetch(`${base.replace(/\/$/, "")}/public/${fetchKind}/${encodeURIComponent(pub[2].split("?")[0])}${window.location.search || ""}`)
        .then(r => (r.ok ? r.json() : Promise.reject(new Error("public " + r.status))))
        .then(d => setPubState((d && d.state) || fallback))
        .catch(() => setPubState(fallback));
    } else if (window.VFP_DB && window.VFP_DB.ready) {
      window.VFP_DB.load().then(remote => setPubState(remote || fallback)).catch(() => setPubState(fallback));
    } else setPubState(fallback);
  }, [isPublic, hash]);

  if (build3d) {
    return (
      <React.Suspense fallback={<div className="muted" style={{ minHeight: "100vh", display: "grid", placeItems: "center", fontSize: 14 }}>Loading 3D…</div>}>
        <PublicBuild3DLazy />
      </React.Suspense>
    );
  }

  if (isPublic) {
    if (!pubState) {
      // widget + chat live inside small embed iframes on contractor sites — a
      // bare centered spinner there, not the big hub card.
      if (pub[1] === "widget" || pub[1] === "chat") {
        return <div style={{ minHeight: "100dvh", display: "grid", placeItems: "center" }}><div className="auth-spinner" style={{ margin: 0 }} /></div>;
      }
      return <div className="hub-shell"><div className="hub-card" style={{ textAlign: "center", maxWidth: 300, margin: "80px auto" }}><div className="auth-spinner" style={{ margin: "0 auto" }} /></div></div>;
    }
    return <PublicRoute hash={hash} state={pubState} />;
  }

  if (auth.loading) {
    return (
      <div className="auth-wrap"><div className="auth-bg" />
        <div className="auth-splash"><LogoMarkLoop size={40} /><span className="display" style={{ fontSize: 20, marginTop: 14 }}>Fence<span style={{ color: "var(--teal)" }}>Works</span></span>
          <div className="auth-spinner" /></div>
      </div>
    );
  }
  if (DEMO.active) {
    // the sandbox has no sign-in gate; if the server says the sandbox is
    // switched off (DEMO_MODE), show that instead of a half-working app
    if (demoOff) return <DemoOff />;
    return <App auth={auth} />;
  }
  if (auth.enabled && !auth.user) return <AuthScreen auth={auth} />;
  return <><RecoveryGate auth={auth} /><App auth={auth} /></>;
}


export { loadState, findCustomer, upsertCustomerInList, nextJobNumber, syncCatalog, emitAutomation, useStore, ComingSoon, App, UpgradeGate, SyncDot, Root, LS_KEY, BUILTIN_PRODUCTS, BLANK, TOOLS, ROUTE_GATE, CRUMB };
